import { Component, useState, useEffect, useRef, useCallback, useMemo } from "react";
import Papa from "papaparse";
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Cell } from "recharts";

const SK = "famcfo_v3";
const MDL = "claude-fable-5";
const CATS = ["Housing","Transportation","Food","Insurance","Utilities","Personal","Family","Debt","Investment","Entertainment","Other"];
// Design tokens — friendly modern neutrals + iris accent. Chart series colors
// (green/accent/yellow) are a validated colorblind-safe categorical trio.
const T = {
  bg:"#F6F6F9",surf:"#FFFFFF",raised:"#F1F1F6",border:"#E6E6EE",borderH:"#C8C8D8",
  text:"#1B1B2B",muted:"#71718C",accent:"#635BFF",accentDim:"rgba(99,91,255,0.08)",
  red:"#E5484D",yellow:"#D97706",blue:"#3B82F6",green:"#189A55",
};

const DEF = {
  income:{
    self:{name:"Bayan",entries:[{id:"di1",amount:0,startDate:"",endDate:""}]},
    spouse:{name:"Spouse",entries:[{id:"di2",amount:0,startDate:"",endDate:""}]},
    anchor:"2026-05-09"
  },
  expenses:[],accounts:[],periodData:{},reminders:[],messages:[],oneTime:[],aiProjections:{},standingRules:[],
};

const RULE_CATS = ["Accounts","Transactions","Spending Patterns","Budget Mechanics","Family Context"];

const STANDING_RULES_SEED = [
  // ── Accounts ──────────────────────────────────────────────────────────────
  {id:"sr01",category:"Accounts",active:true,source:"seed",createdAt:"2026-01-01",
   rule:"Spending chq (...5058300) = day-to-day debit purchases. Represents fully discretionary cash spending."},
  {id:"sr02",category:"Accounts",active:true,source:"seed",createdAt:"2026-01-01",
   rule:"Payments chq (...5062963) = fully earmarked for bills. Never treat balance as savings or liquid. Always already committed — treat as $0 available."},
  {id:"sr03",category:"Accounts",active:true,source:"seed",createdAt:"2026-01-01",
   rule:"Savings (...5110119) = target savings account but frequently used for discretionary. Track net change only, not the balance. Balance alone does not reflect true savings health."},
  {id:"sr04",category:"Accounts",active:true,source:"seed",createdAt:"2026-01-01",
   rule:"CC1 / Avion Prestige Visa (...2348) = high-interest card being actively paid down. Large payments to this card are debt reduction, not discretionary spending. Interest charges are mandatory cost until balance is cleared."},
  {id:"sr05",category:"Accounts",active:true,source:"seed",createdAt:"2026-01-01",
   rule:"LOC #1 (...2752001) draws that appear as deposits in chequing = funding, NOT income. Never count LOC draws as earned income or savings."},
  {id:"sr06",category:"Accounts",active:true,source:"seed",createdAt:"2026-01-01",
   rule:"LOC #2 (...0103001) = pool/Ponderosa loan. Interest $150–161/month appears in Payments account. Treat as a committed fixed cost with no budget line."},
  {id:"sr07",category:"Accounts",active:true,source:"seed",createdAt:"2026-01-01",
   rule:"Kids account transfers in/out of Savings = net-zero internal movements. Exclude entirely from spending analysis. Do not count as income or expense."},
  // ── Transactions ──────────────────────────────────────────────────────────
  {id:"sr08",category:"Transactions",active:true,source:"seed",createdAt:"2026-01-01",
   rule:"FETCH appearing as two identical charges per billing cycle = two dogs (Kobe + second dog). Always normal. Never flag as duplicate or anomaly."},
  {id:"sr09",category:"Transactions",active:true,source:"seed",createdAt:"2026-01-01",
   rule:"Multiple mortgage line items in the same period = normal. Do not flag as duplicates. The budget has multiple loan components."},
  {id:"sr10",category:"Transactions",active:true,source:"seed",createdAt:"2026-01-01",
   rule:"Queens Ave $1,875 on the 1st of each month = investment property mortgage payment. Not personal household spending. Do not include in discretionary analysis."},
  {id:"sr11",category:"Transactions",active:true,source:"seed",createdAt:"2026-01-01",
   rule:"Family Help / Mona Balche = $500/month budgeted support payment. Categorize as Family. Not charity, not a gift, not discretionary — it is a committed fixed payment."},
  {id:"sr12",category:"Transactions",active:true,source:"seed",createdAt:"2026-01-01",
   rule:"Rachel Larusso e-transfers = Kobe dog boarding. Recurring as needed when family travels. Always normal — never flag as anomaly."},
  {id:"sr13",category:"Transactions",active:true,source:"seed",createdAt:"2026-01-01",
   rule:"Canuck Sports / Scorpions YVC transactions = Lana volleyball fees and equipment. Unbudgeted but recurring annually. Treat as a known seasonal category."},
  {id:"sr14",category:"Transactions",active:true,source:"seed",createdAt:"2026-01-01",
   rule:"Klarna payments = debt reduction installments. Not discretionary spending. Categorize alongside CC payments, not shopping."},
  {id:"sr15",category:"Transactions",active:true,source:"seed",createdAt:"2026-01-01",
   rule:"Costco transactions = categorize as Groceries, not Shopping, regardless of what was purchased."},
  {id:"sr16",category:"Transactions",active:true,source:"seed",createdAt:"2026-01-01",
   rule:"PALLETT VALO LLP deposits = extraordinary legal/income event. Always exclude from regular spending and savings analysis. Treat as a one-time item only."},
  {id:"sr17",category:"Transactions",active:true,source:"seed",createdAt:"2026-01-01",
   rule:"Overdraft fees in Payments account = avoidable cost caused by bill transfer timing mismatch. Flag every occurrence — this is a process problem, not a spending problem."},
  {id:"sr18",category:"Transactions",active:true,source:"seed",createdAt:"2026-01-01",
   rule:"Same merchant + same amount appearing in two different accounts within 24 hours = potential duplicate transaction. Always flag for manual review before including in totals."},
  {id:"sr19",category:"Transactions",active:true,source:"seed",createdAt:"2026-01-01",
   rule:"Small inbound e-transfers from individuals (under $100) = likely reimbursements. Do not count as income. Ask for context if unrecognized."},
  // ── Spending Patterns ─────────────────────────────────────────────────────
  {id:"sr20",category:"Spending Patterns",active:true,source:"seed",createdAt:"2026-01-01",
   rule:"Eating out = #1 controllable overspend. Historically running 3–4× the implicit budget every period. Any period where eating out is under $800 total is a meaningful improvement worth noting positively."},
  {id:"sr21",category:"Spending Patterns",active:true,source:"seed",createdAt:"2026-01-01",
   rule:"Groceries = structural overspend. Consistent actuals of $1,300–1,400/period vs $700 budget. Track the trend direction, not just the absolute amount. Improvement matters even if still over budget."},
  {id:"sr22",category:"Spending Patterns",active:true,source:"seed",createdAt:"2026-01-01",
   rule:"$1,800 biweekly Spending Cash allocation covers less than 25% of actual discretionary spending. The gap between budget and actual is the real story — focus analysis there, not on individual line items."},
  {id:"sr23",category:"Spending Patterns",active:true,source:"seed",createdAt:"2026-01-01",
   rule:"Winners/HomeSense = recurring impulsive shopping pattern. Flag when 3 or more visits appear in a single period, or when combined total exceeds $300 in a period."},
  {id:"sr24",category:"Spending Patterns",active:true,source:"seed",createdAt:"2026-01-01",
   rule:"Lana birthday spending spans 2–3 periods in March–April each year (gifts: UGG, Aritzia, Birkenstock, Pandora, Sandbox VR, Coach, restaurants, etc.). Treat as an annual one-time category — do not fold into recurring discretionary averages."},
  {id:"sr25",category:"Spending Patterns",active:true,source:"seed",createdAt:"2026-01-01",
   rule:"Eid celebration = annual one-time spending event, typically $1,000–1,500. Do not count as ongoing discretionary trend when calculating period averages."},
  {id:"sr26",category:"Spending Patterns",active:true,source:"seed",createdAt:"2026-01-01",
   rule:"Spain 2026 trip (Bladerunner Mallorca, etc.) = planned one-time travel. Track separately. Exclude from eating out and discretionary averages when analyzing spending patterns."},
  // ── Budget Mechanics ──────────────────────────────────────────────────────
  {id:"sr27",category:"Budget Mechanics",active:true,source:"seed",createdAt:"2026-01-01",
   rule:"Monthly-frequency bills appear once per 2-week period. Always compare against the full monthly budget amount — not the biweekly equivalent. A $716 property tax payment is on budget, not 2× over."},
  {id:"sr28",category:"Budget Mechanics",active:true,source:"seed",createdAt:"2026-01-01",
   rule:"Monthly bills cluster on the 1st: RESP, 407 ETR, Property Tax, Bell, Queens Ave, Family Help all hit together. Any period containing the 1st will show elevated Payments outflow — this is expected, not alarming."},
  {id:"sr29",category:"Budget Mechanics",active:true,source:"seed",createdAt:"2026-01-01",
   rule:"LOC #1 interest now ~$21/month (down from $700+/month in 2024 after major payoffs). A significant achievement. Flag if this starts rising again as an early warning sign."},
  {id:"sr30",category:"Budget Mechanics",active:true,source:"seed",createdAt:"2026-01-01",
   rule:"Savings account balance can be inflated by LOC draws without representing real savings. Always cross-reference savingsDeltaWithCC (true wealth change including CC movement) as the authoritative savings metric."},
  {id:"sr31",category:"Budget Mechanics",active:true,source:"seed",createdAt:"2026-01-01",
   rule:"TFSA withdrawal in a period = real overspend signal regardless of what the savings balance shows. If TFSA was drawn, add that amount to the period's true deficit. Flag prominently."},
  {id:"sr32",category:"Budget Mechanics",active:true,source:"seed",createdAt:"2026-01-01",
   rule:"407 ETR has historically run over budget (actual $305 vs $100 budget in Mar 2026). May reflect outdated budget line or catch-up bills. Watch across multiple periods before concluding — single-period spikes may be cumulative."},
  // ── Family Context ────────────────────────────────────────────────────────
  {id:"sr33",category:"Family Context",active:true,source:"seed",createdAt:"2026-01-01",
   rule:"Lana = daughter, competitive volleyball player. Annual volleyball fees (Canuck Sports, Scorpions YVC) are unbudgeted but expected. Birthday in late March generates elevated multi-period spending annually."},
  {id:"sr34",category:"Family Context",active:true,source:"seed",createdAt:"2026-01-01",
   rule:"Kareem = son. Driving lessons were confirmed one-time (PayPal $625, March 2026). Basketball fees (Kareem Bball) are recurring. Any new large Kareem-related expense should be flagged for context."},
  {id:"sr35",category:"Family Context",active:true,source:"seed",createdAt:"2026-01-01",
   rule:"Kobe = dog. FETCH ×2 per period is always normal (second charge = second dog or recurring service). Rachel Larusso e-transfers = boarding when family is away."},
  {id:"sr36",category:"Family Context",active:true,source:"seed",createdAt:"2026-01-01",
   rule:"Rasha's tax refund typically arrives in April each year (~$4,000 in 2026). When it lands, record as one-time income — do not project it as recurring. Rasha's salary follows a date-ranged schedule, not a flat annual amount."},
];

// ── Helpers ────────────────────────────────────────────────────────────────
const D = iso => new Date(iso+"T12:00:00");
const isoD = d => d.toISOString().slice(0,10);
const addDays = (d,n) => { const x=new Date(d); x.setDate(x.getDate()+n); return x; };
const uid = () => Math.random().toString(36).slice(2,10);
const MOS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function fmtPeriod(s,e) {
  const sd=D(s),ed=D(e);
  return sd.getMonth()===ed.getMonth()
    ? `${MOS[sd.getMonth()]} ${sd.getDate()}–${ed.getDate()}`
    : `${MOS[sd.getMonth()]} ${sd.getDate()} – ${MOS[ed.getMonth()]} ${ed.getDate()}`;
}
function fmtM(n,signed=false) {
  if (n==null||isNaN(n)) return "—";
  const abs=Math.abs(n).toLocaleString("en-CA",{minimumFractionDigits:2,maximumFractionDigits:2});
  if (signed) return (n>=0?"+":"-")+"$"+abs;
  return (n<0?"-":"")+"$"+abs;
}
function genPeriods(anchor,back=24,fwd=26) {
  const a=D(anchor); const ps=[];
  for (let i=-back; i<=fwd; i++) {
    const s=isoD(addDays(a,i*14)),e=isoD(addDays(D(s),13));
    ps.push({key:s,start:s,end:e,label:fmtPeriod(s,e),future:D(s)>new Date()});
  }
  return ps;
}
function expAmt(exp,p) {
  if (!exp.active) return 0;
  const ps=D(p.start),pe=D(p.end);
  if (exp.start&&D(exp.start)>pe) return 0;
  if (exp.end&&D(exp.end)<ps) return 0;
  return exp.freq==="monthly"?exp.amount/2:exp.amount;
}
function totExp(exps,p) { return exps.reduce((s,e)=>s+expAmt(e,p),0); }

// ── Income helpers ─────────────────────────────────────────────────────────
function getPersonIncome(entries,date) {
  if (!entries||!entries.length) return 0;
  const d=date instanceof Date?date:D(date);
  const matches=entries.filter(e=>{
    const after=!e.startDate||D(e.startDate)<=d;
    const before=!e.endDate||D(e.endDate)>=d;
    return after&&before;
  });
  if (!matches.length) return 0;
  matches.sort((a,b)=>(b.startDate||"0000").localeCompare(a.startDate||"0000"));
  return matches[0].amount||0;
}
function getTotalIncome(income,date) {
  return getPersonIncome(income?.self?.entries,date)+getPersonIncome(income?.spouse?.entries,date);
}

function getActual(state,pk) {
  const pd=state.periodData[pk];
  if (pd?.savingsDelta!=null) return pd.savingsDelta;
  if (pd?.manualSavings!=null) return pd.manualSavings;
  const sav=state.accounts.filter(a=>a.include&&a.trackSavings);
  if (!sav.length) return null;
  return sav.reduce((s,a)=>{
    const txns=(a.transactions||[]).filter(t=>t.periodKey===pk);
    return s+txns.reduce((ss,t)=>ss+t.amount,0);
  },0);
}

function buildMetrics(state,periods) {
  const ot=state.oneTime||[];
  const pdMap=state.periodData||{};
  return periods.reduce((m,p,idx)=>{
    // Budgeted amounts
    const baseInc=getTotalIncome(state.income,D(p.start));
    const otPeriod=ot.filter(o=>o.periodKey===p.key);
    const otInc=otPeriod.filter(o=>o.type==="income").reduce((s,o)=>s+o.amount,0);
    const otExp=otPeriod.filter(o=>o.type==="expense").reduce((s,o)=>s+o.amount,0);
    const otSavInc=otPeriod.filter(o=>o.type==="income"&&(o.source==="Savings"||!o.source)).reduce((s,o)=>s+o.amount,0);
    const otSavExp=otPeriod.filter(o=>o.type==="expense"&&(o.source==="Savings"||!o.source)).reduce((s,o)=>s+o.amount,0);
    const inc=baseInc+otInc, exp=totExp(state.expenses,p)+otExp, bSav=inc-exp;
    const bSavSavings=baseInc+otSavInc-totExp(state.expenses,p)-otSavExp;
    // Current period snapshot
    const pd=pdMap[p.key]||{};
    const savingsBalance=pd.savingsBalance??null;
    const ccBalance=pd.ccBalance??null;
    const locBalance=pd.locBalance??null;
    const ppBalance=pd.paymentPlanBalance??null;
    // Find most recent prior period that has balance data
    const priorPd=periods.slice(0,idx).reverse()
      .map(pp=>pdMap[pp.key]||{})
      .find(pp=>pp.savingsBalance!=null)||{};
    // Deltas from snapshots — legacy savingsDelta as fallback if no prior snapshot
    const savingsDelta=(savingsBalance!=null&&priorPd.savingsBalance!=null)
      ?(savingsBalance-priorPd.savingsBalance):(pd.savingsDelta??null);
    const ccDelta=(ccBalance!=null&&priorPd.ccBalance!=null)
      ?(ccBalance-priorPd.ccBalance):null;
    const locDelta=(locBalance!=null&&priorPd.locBalance!=null)
      ?(locBalance-priorPd.locBalance):null;
    // True wealth Δ = Sav Δ − CC increase − LOC increase
    // (CC/LOC going up = more debt = wealth decrease)
    const trueWealthDelta=(savingsDelta!=null&&ccDelta!=null&&locDelta!=null)
      ?(savingsDelta-ccDelta-locDelta)
      :savingsDelta;
    const act=trueWealthDelta;
    m[p.key]={inc,exp,bSav,bSavSavings,act,delta:act!=null?act-bSav:null,
      savingsBalance,ccBalance,locBalance,ppBalance,
      savingsDelta,ccDelta,locDelta,trueWealthDelta,
      notes:pd.notes||"",otItems:otPeriod};
    return m;
  },{});
}

function buildSys(state,periods,metrics) {
  const inc=state.income, now=new Date();
  const cur=periods.find(p=>D(p.start)<=now&&D(p.end)>=now);
  const past=periods.filter(p=>D(p.end)<=now).slice(-8);
  // Transactions: current period (all) + last closed period (all) + older (20)
  const lastClosed=past[past.length-1];
  const curTxns=[], lastTxns=[], olderTxns=[];
  for (const a of state.accounts.filter(ac=>ac.include))
    for (const t of (a.transactions||[])) {
      const row={...t,acct:a.label||a.name};
      if (cur&&t.periodKey===cur.key) curTxns.push(row);
      else if (lastClosed&&t.periodKey===lastClosed.key) lastTxns.push(row);
      else olderTxns.push(row);
    }
  curTxns.sort((a,b)=>b.date.localeCompare(a.date));
  lastTxns.sort((a,b)=>b.date.localeCompare(a.date));
  olderTxns.sort((a,b)=>b.date.localeCompare(a.date));
  const fmtTxn=t=>`${t.date}|${t.acct}|${t.description}|${fmtM(t.amount,true)}`;
  const openR=state.reminders.filter(r=>!r.resolved);
  const ot=state.oneTime||[];
  const otRecent=ot.filter(o=>{const p=periods.find(x=>x.key===o.periodKey);return p&&D(p.end)>=addDays(new Date(),-90);});
  const fmtEntries=entries=>(entries||[]).map(e=>`${fmtM(e.amount)}/period${e.startDate?` from ${e.startDate}`:""}${e.endDate?` to ${e.endDate}`:""}`).join("; ")||"not set";
  const biweeklyExps=state.expenses.filter(e=>e.active&&e.freq==="biweekly");
  const monthlyExps=state.expenses.filter(e=>e.active&&e.freq==="monthly");
  return `You are FamCFO, personal AI CFO for ${inc.self.name}. Full financial context below.

INCOME SCHEDULE:
${inc.self.name}: ${fmtEntries(inc.self.entries)}
${inc.spouse.name}: ${fmtEntries(inc.spouse.entries)}
Current combined: ${fmtM(getTotalIncome(state.income,now))}/period

BUDGET — BIWEEKLY ITEMS (appear every period, compare against amount shown):
${biweeklyExps.map(e=>`- ${e.name}: ${fmtM(e.amount)}/period [${e.cat}]`).join("\n")||"None"}

BUDGET — MONTHLY ITEMS (appear ONCE per month — compare transaction against the MONTHLY amount below, NOT half of it. A full monthly charge in a single period is on budget):
${monthlyExps.map(e=>`- ${e.name}: ${fmtM(e.amount)}/month on day ${e.dom||"?"} [${e.cat}]`).join("\n")||"None"}

ONE-TIME ITEMS (recent 90 days + upcoming):
${otRecent.length?otRecent.map(o=>{const p=periods.find(x=>x.key===o.periodKey);return`- [${p?.label||o.periodKey}] ${o.type==="income"?"💰":"💸"} ${o.name}: ${fmtM(o.amount)} [${o.cat}]${o.note?" — "+o.note:""}`;}).join("\n"):"None"}

PERIOD HISTORY (last 8 closed periods — balances and calculated deltas):
${past.map(p=>{const m=metrics[p.key];if(!m)return"";
  return`- ${p.label}:
    Balances: Sav ${m.savingsBalance!=null?fmtM(m.savingsBalance):"n/a"} | CC ${m.ccBalance!=null?fmtM(m.ccBalance):"n/a"} | LOC ${m.locBalance!=null?fmtM(m.locBalance):"n/a"} | PP ${m.ppBalance!=null?fmtM(m.ppBalance):"n/a"}
    Deltas:   Sav Δ ${m.savingsDelta!=null?fmtM(m.savingsDelta,true):"n/a"} | CC Δ ${m.ccDelta!=null?fmtM(m.ccDelta,true):"n/a"} | LOC Δ ${m.locDelta!=null?fmtM(m.locDelta,true):"n/a"}
    Result:   True Wealth Δ ${m.trueWealthDelta!=null?fmtM(m.trueWealthDelta,true):"n/a"} | Budgeted ${fmtM(m.bSav)} | Variance ${m.delta!=null?fmtM(m.delta,true):"n/a"}${m.notes?" ["+m.notes+"]":""}`;
}).join("\n")||"No history — enter period-end balances via the Summary tab Edit buttons"}

OPEN ACTIONS: ${openR.length?openR.map(r=>`[${r.periodKey||"?"}] ${r.text}`).join("; "):"None"}

ACCOUNTS: ${state.accounts.map(a=>`${a.label||a.name}(${a.include?"in":"out"}${a.trackSavings?",SAVINGS":""})`).join(", ")||"None"}

CURRENT PERIOD TRANSACTIONS${cur?` — ${cur.label} (${curTxns.length} total)`:""} — ALL shown:
${curTxns.length?curTxns.map(fmtTxn).join("\n"):"None yet — period just started or no upload for this period"}

LAST CLOSED PERIOD TRANSACTIONS${lastClosed?` — ${lastClosed.label} (${lastTxns.length} total)`:""} — ALL shown:
${lastTxns.length?lastTxns.map(fmtTxn).join("\n"):"None"}

OLDER TRANSACTIONS (prior periods, most recent 20):
${olderTxns.slice(0,20).map(fmtTxn).join("\n")||"None"}

STANDING RULES (${(state.standingRules||[]).filter(r=>r.active).length} active):
${(state.standingRules||[]).filter(r=>r.active).map((r,i)=>`${i+1}. [${r.category}] ${r.rule}`).join("\n")||"No standing rules set — go to the Rules tab to import the default set."}

Flag open actions at start of any analysis. Be specific, data-driven, reference real transactions.

ANALYSIS METHODOLOGY — ALWAYS FOLLOW THIS:
Period analysis is balance-based, not transaction-based. The correct process:
1. Ask the user for 4 end-of-period balance snapshots: Savings, Total CC, LOC, Payment Plan (Payments chq)
2. Apply a 1-2 day offset tolerance when matching transfers between accounts (e.g. a savings withdrawal on day 14 may appear as a CC payment on day 15 or 16 — these are the same cash movement)
3. Calculate deltas vs the PRIOR period's closing balances (already done in period history above)
4. Derive True Wealth Δ = Savings Δ − CC balance increase − LOC balance increase
   (CC balance going UP means more debt = wealth decrease; CC going DOWN = debt paid = wealth increase)
5. Calculate Cash Draw = Income received − True Wealth Δ (what you actually spent)
6. Compare Cash Draw to Budgeted Expenses to find overspend/underspend
7. Cross-reference with transactions and standing rules to explain WHERE the variance came from

PERIOD DATA UPDATE CAPABILITY:
When you have collected the 4 end-of-period balances from the user (with their confirmation), output this tag — it will be applied to the period data after user confirmation and will NEVER overwrite existing non-null values:
[UPDATE_PERIOD:YYYY-MM-DD]{"savingsBalance":X,"ccBalance":Y,"locBalance":Z,"paymentPlanBalance":W,"notes":"optional summary"}[/UPDATE_PERIOD]
Use the period START date (YYYY-MM-DD) as the key. Only include values the user has confirmed. Omit any field the user has not provided. Balance fields (savingsBalance/ccBalance/locBalance/paymentPlanBalance) will NEVER overwrite existing non-null values. The notes field WILL overwrite any existing note — use it to add a concise period summary (100 chars max).

RULE SUGGESTION CAPABILITY:
Based on what you observe in uploads, period history, or spending patterns, you may suggest new standing rules using this tag:
[SUGGEST_RULE:Category]Rule text here[/SUGGEST_RULE]
Valid categories: ${RULE_CATS.join(", ")}
Only suggest rules that represent durable patterns or classification logic — not one-time observations. The user will approve or dismiss each suggestion. You may suggest multiple rules in one response. Never suggest a rule that duplicates an existing one above.

ACTION ITEM CAPABILITY:
You may propose action items for the Actions tab when your analysis surfaces something the user needs to do later (reconcile a balance, chase a refund, check a bill next period). Use this tag:
[ADD_ACTION:YYYY-MM-DD]Action text here[/ADD_ACTION]
The :YYYY-MM-DD suffix is OPTIONAL — include it (a period START date) to pin the action to a period, or use plain [ADD_ACTION]...[/ADD_ACTION] for no period. Keep each action to one concrete, checkable task under 120 chars. The user approves or dismisses each one — nothing is written without confirmation. Never propose an action that duplicates one already in OPEN ACTIONS above.

AI PROJECTION COLUMN — YOUR WRITE ACCESS:
You have write access to ONE column only: "AI Proj. Balance" in the 12-month projection table.
Income, expenses, history, budgets and balances are READ-ONLY for you — you may only propose changes to period data, standing rules and action items via the tags above.
Use this column when uploads or spending analysis gives you insight that the mechanical projection is too optimistic or pessimistic.
To update it, include a JSON block anywhere in your response — it will be auto-applied and hidden from the chat:
[AI_PROJ]{"2026-06-06":18500,"2026-06-20":17200}[/AI_PROJ]
Use period START dates (YYYY-MM-DD) as keys. Only include periods you want to update. Omit periods where you have no better insight than the mechanical model.
Current AI projections: ${Object.keys(state.aiProjections||{}).length?JSON.stringify(state.aiProjections):"None set — mechanical projection is in use for all periods"}`;
}

// ── Storage ────────────────────────────────────────────────────────────────
async function loadSt(token) {
  try {
    const res = await fetch('/.netlify/functions/state', { headers: { 'Authorization': `Bearer ${token}` } })
    if (res.status === 401) return null
    const data = await res.json()
    if (data) {
      const merged = { ...DEF, ...data }
      if (merged.income?.self?.amount !== undefined) {
        merged.income.self = { name: merged.income.self.name || 'Bayan', entries: [{ id: 'mi1', amount: merged.income.self.amount || 0, startDate: '', endDate: '' }] }
      }
      if (merged.income?.spouse?.amount !== undefined) {
        merged.income.spouse = { name: merged.income.spouse.name || 'Spouse', entries: [{ id: 'mi2', amount: merged.income.spouse.amount || 0, startDate: '', endDate: '' }] }
      }
      // Model migration: Sonnet 4.6 was replaced by Sonnet 5 in the selector
      if (merged.model === 'claude-sonnet-4-6') merged.model = 'claude-sonnet-5'
      return merged
    }
  } catch (e) { console.error('loadSt error:', e) }
  return { ...DEF }
}
async function saveSt(s, token) {
  if (!s?.income?.anchor) return
  try {
    const res = await fetch('/.netlify/functions/state', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(s),
    })
    if (!res.ok) console.error('saveSt failed:', res.status, await res.text())
  } catch (e) { console.error('saveSt error:', e) }
}

// ── Shared UI ──────────────────────────────────────────────────────────────
const sx = {
  input:{background:T.surf,border:`1.5px solid ${T.border}`,borderRadius:10,padding:"7px 12px",color:T.text,fontSize:12,fontFamily:"'Geist',sans-serif",outline:"none",width:"100%"},
  card:{background:T.surf,border:`1px solid ${T.border}`,borderRadius:16,boxShadow:"0 1px 2px rgba(27,27,43,0.04), 0 12px 32px -16px rgba(27,27,43,0.10)"},
  th:{padding:"8px 10px",textAlign:"left",fontSize:10,color:T.muted,fontFamily:"'Geist',sans-serif",textTransform:"uppercase",letterSpacing:"0.07em",fontWeight:500,whiteSpace:"nowrap"},
  td:{padding:"8px 10px",fontSize:12,fontFamily:"'Geist',sans-serif"},
};
const Inp=({value,onChange,placeholder,type="text",style:s})=>(
  <input type={type} value={value??""} onChange={e=>onChange(e.target.value)} placeholder={placeholder} style={{...sx.input,...s}}/>
);
const Sel=({value,onChange,options,style:s})=>(
  <select value={value??""} onChange={e=>onChange(e.target.value)}
    style={{...sx.input,appearance:"none",backgroundImage:`url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%236b84a8'/%3E%3C/svg%3E")`,backgroundRepeat:"no-repeat",backgroundPosition:"right 8px center",...s}}>
    {options.map(o=>typeof o==="string"?<option key={o} value={o}>{o}</option>:<option key={o.v} value={o.v}>{o.l}</option>)}
  </select>
);
const Btn=({children,onClick,v="p",sm,disabled,style:s})=>{
  const vs={
    p:{background:T.accent,color:"#fff",border:"1px solid transparent",boxShadow:"0 2px 8px -2px rgba(99,91,255,0.45)"},
    g:{background:"transparent",color:T.muted,border:`1px solid ${T.border}`},
    d:{background:"transparent",color:T.red,border:`1px solid ${T.red}44`},
    o:{background:T.surf,color:T.text,border:`1px solid ${T.border}`,boxShadow:"0 1px 2px rgba(27,27,43,0.06)"},
  };
  return <button onClick={onClick} disabled={disabled} style={{padding:sm?"4px 12px":"8px 16px",fontSize:sm?11:12.5,fontFamily:"'Geist',sans-serif",fontWeight:600,borderRadius:999,cursor:disabled?"not-allowed":"pointer",opacity:disabled?0.45:1,transition:"opacity .15s, transform .1s",display:"inline-flex",alignItems:"center",gap:4,...vs[v],...s}}>{children}</button>;
};
const Lbl=({children,style:s})=><div style={{fontSize:10,fontFamily:"'Geist',sans-serif",color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5,...s}}>{children}</div>;
const Num=({children,c,style:s})=><span style={{fontFamily:"'Geist',sans-serif",color:c||T.text,...s}}>{children}</span>;
const Card=({children,style:s,className})=><div className={className} style={{...sx.card,...s}}>{children}</div>;

// ── Error Boundary ─────────────────────────────────────────────────────────
// Catches render/commit-phase throws so one bad tab never blanks the whole app.
// Sourcemaps are on (vite.config.js) so the stack below points at real lines.
class ErrBoundary extends Component {
  constructor(p){ super(p); this.state={err:null,info:null}; }
  static getDerivedStateFromError(err){ return {err}; }
  componentDidCatch(err,info){ console.error("ErrBoundary caught:",err,info?.componentStack); this.setState({info}); }
  render(){
    if(!this.state.err) return this.props.children;
    const {err,info}=this.state;
    return (
      <Card style={{padding:16,border:`1px solid ${T.red}55`,background:"rgba(220,38,38,0.04)",display:"flex",flexDirection:"column",gap:10}}>
        <div style={{fontFamily:"'Outfit',sans-serif",fontSize:14,fontWeight:700,color:T.red}}>This tab hit an error</div>
        <div style={{fontSize:11,color:T.text,fontFamily:"'Geist',sans-serif"}}>Your data is safe — nothing was lost. Switch tabs or reload. Copy the details below if it keeps happening.</div>
        <pre style={{background:T.raised,border:`1px solid ${T.border}`,borderRadius:8,padding:"10px 12px",fontFamily:"'Geist',sans-serif",fontSize:10,lineHeight:1.6,color:T.muted,overflowX:"auto",maxHeight:220,whiteSpace:"pre-wrap"}}>
{String(err?.stack||err)}{info?.componentStack?`\n\nComponent stack:${info.componentStack}`:""}
        </pre>
        <div style={{display:"flex",gap:8}}>
          <Btn v="o" onClick={()=>this.setState({err:null,info:null})}>Try again</Btn>
          <Btn v="g" onClick={()=>navigator.clipboard?.writeText(String(err?.stack||err)+(info?.componentStack||"")).catch(()=>{})}>Copy details</Btn>
        </div>
      </Card>
    );
  }
}

// ── Income Entry Modal ─────────────────────────────────────────────────────
function IncomeEntryModal({personName,entry,onSave,onClose}) {
  const [f,setF]=useState(entry||{id:uid(),amount:"",startDate:"",endDate:""});
  const valid=+f.amount>0;
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(27,27,43,0.40)",backdropFilter:"blur(4px)",WebkitBackdropFilter:"blur(4px)",zIndex:200,display:"flex",alignItems:"flex-end",justifyContent:"center"}}>
      <Card className="sheet" style={{maxWidth:400,padding:18,display:"flex",flexDirection:"column",gap:12}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <h3 style={{fontFamily:"'Outfit',sans-serif",fontSize:14,fontWeight:700,color:T.text}}>{entry?"Edit":"Add"} Income — {personName}</h3>
          <Btn v="g" sm onClick={onClose}>✕</Btn>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          <div style={{gridColumn:"1/-1"}}><Lbl>Biweekly Net Amount ($)</Lbl><Inp type="number" value={f.amount} onChange={v=>setF(p=>({...p,amount:v}))} placeholder="0.00"/></div>
          <div><Lbl>Start Date (blank = always)</Lbl><Inp type="date" value={f.startDate} onChange={v=>setF(p=>({...p,startDate:v}))}/></div>
          <div><Lbl>End Date (blank = ongoing)</Lbl><Inp type="date" value={f.endDate} onChange={v=>setF(p=>({...p,endDate:v}))}/></div>
        </div>
        <p style={{fontSize:10,color:T.muted,fontFamily:"'Geist',sans-serif"}}>Leave both dates blank for an amount that always applies. Use start/end to model income changes or seasonal variations.</p>
        <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
          <Btn v="g" onClick={onClose}>Cancel</Btn>
          <Btn onClick={()=>valid&&onSave({...f,amount:+f.amount})} disabled={!valid}>Save</Btn>
        </div>
      </Card>
    </div>
  );
}

// ── Income Tab ─────────────────────────────────────────────────────────────
function IncomeTab({state,update}) {
  const [editPerson,setEditPerson]=useState(null);
  const now=new Date();
  const curInc=getTotalIncome(state.income,now);

  const setName=(key,name)=>update({income:{...state.income,[key]:{...state.income[key],name}}});
  const saveEntry=(key,entry)=>{
    const person=state.income[key];
    const entries=person.entries||[];
    const exists=entries.find(e=>e.id===entry.id);
    update({income:{...state.income,[key]:{...person,entries:exists?entries.map(e=>e.id===entry.id?entry:e):[...entries,entry]}}});
    setEditPerson(null);
  };
  const delEntry=(key,id)=>{
    const person=state.income[key];
    update({income:{...state.income,[key]:{...person,entries:(person.entries||[]).filter(e=>e.id!==id)}}});
  };
  return (
    <div style={{maxWidth:620,margin:"0 auto",display:"flex",flexDirection:"column",gap:14}}>
      {editPerson&&<IncomeEntryModal personName={state.income[editPerson.key].name} entry={editPerson.entry}
        onSave={entry=>saveEntry(editPerson.key,entry)} onClose={()=>setEditPerson(null)}/>}
      <h2 style={{fontFamily:"'Outfit',sans-serif",fontSize:17,fontWeight:700,color:T.text}}>Income Setup</h2>

      {["self","spouse"].map(key=>{
        const person=state.income[key]||{name:"",entries:[]};
        const entries=person.entries||[];
        return (
          <Card key={key} style={{padding:14}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <Lbl style={{marginBottom:0}}>{key==="self"?"You":"Spouse / Partner"}</Lbl>
                <input value={person.name||""} onChange={e=>setName(key,e.target.value)}
                  style={{...sx.input,width:130,padding:"3px 8px",fontSize:12,fontWeight:600}}
                  placeholder={key==="self"?"Your name":"Partner name"}/>
              </div>
              <Btn sm onClick={()=>setEditPerson({key,entry:null})}>+ Add Entry</Btn>
            </div>
            {!entries.length&&<p style={{fontSize:11,color:T.muted,fontFamily:"'Geist',sans-serif"}}>No income entries yet.</p>}
            {entries.length>0&&(
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
                <thead><tr style={{borderBottom:`1px solid ${T.border}`}}>
                  {["Biweekly Amount","Start","End",""].map(h=><th key={h} style={sx.th}>{h}</th>)}
                </tr></thead>
                <tbody>{[...entries].sort((a,b)=>(b.startDate||"0000").localeCompare(a.startDate||"0000")).map(e=>{
                  const isCur=getPersonIncome([e],now)>0;
                  return (
                    <tr key={e.id} style={{borderBottom:`1px solid ${T.border}22`,opacity:isCur?1:0.5}}>
                      <td style={{...sx.td,color:T.accent,fontWeight:600}}>{fmtM(e.amount)}</td>
                      <td style={{...sx.td,color:T.muted}}>{e.startDate||"—"}</td>
                      <td style={{...sx.td,color:T.muted}}>{e.endDate||"ongoing"}</td>
                      <td style={{...sx.td,whiteSpace:"nowrap"}}>
                        <Btn sm v="g" onClick={()=>setEditPerson({key,entry:e})}>Edit</Btn>{" "}
                        <Btn sm v="d" onClick={()=>delEntry(key,e.id)}>Del</Btn>
                      </td>
                    </tr>
                  );
                })}</tbody>
              </table>
            )}
          </Card>
        );
      })}

      <Card style={{padding:14,border:`1px solid ${T.accent}33`,background:T.accentDim}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline"}}>
          <span style={{fontSize:12,color:T.muted,fontFamily:"'Geist',sans-serif"}}>Combined biweekly — current period</span>
          <Num c={T.accent} style={{fontSize:24,fontWeight:600}}>{fmtM(curInc)}</Num>
        </div>
        <div style={{fontSize:10,color:T.muted,fontFamily:"'Geist',sans-serif",marginTop:3}}>
          Monthly equiv: {fmtM(curInc*2)} · Annual: {fmtM(curInc*26)}
        </div>
      </Card>
    </div>
  );
}

// ── Period Edit Modal ──────────────────────────────────────────────────────
function PeriodEditModal({pk,label,pd,priorPd,onSave,onClose}) {
  const [f,setF]=useState({
    savingsBalance:pd?.savingsBalance!=null?String(pd.savingsBalance):"",
    ccBalance:pd?.ccBalance!=null?String(pd.ccBalance):"",
    locBalance:pd?.locBalance!=null?String(pd.locBalance):"",
    paymentPlanBalance:pd?.paymentPlanBalance!=null?String(pd.paymentPlanBalance):"",
    notes:pd?.notes||"",
  });
  const upd=(k,v)=>setF(p=>({...p,[k]:v}));
  const n=v=>v===""?null:+v;
  // Calculate deltas for preview
  const sav=n(f.savingsBalance), cc=n(f.ccBalance), loc=n(f.locBalance);
  const savD=(sav!=null&&priorPd?.savingsBalance!=null)?sav-priorPd.savingsBalance:null;
  const ccD=(cc!=null&&priorPd?.ccBalance!=null)?cc-priorPd.ccBalance:null;
  const locD=(loc!=null&&priorPd?.locBalance!=null)?loc-priorPd.locBalance:null;
  const trueW=(savD!=null&&ccD!=null&&locD!=null)?savD-ccD-locD:savD;
  const hasCalc=savD!=null||ccD!=null||locD!=null;
  const col=v=>v==null?T.muted:v>=0?T.green:T.red;
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(27,27,43,0.40)",backdropFilter:"blur(4px)",WebkitBackdropFilter:"blur(4px)",zIndex:200,display:"flex",alignItems:"flex-end",justifyContent:"center"}}>
      <Card className="sheet" style={{maxWidth:500,padding:18,display:"flex",flexDirection:"column",gap:12}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
          <div>
            <h3 style={{fontFamily:"'Outfit',sans-serif",fontSize:14,fontWeight:700,color:T.text}}>Period-End Balances</h3>
            <div style={{fontSize:11,color:T.muted,fontFamily:"'Geist',sans-serif"}}>{label}</div>
          </div>
          <Btn v="g" sm onClick={onClose}>✕</Btn>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          <div><Lbl>Savings Balance ($)</Lbl><Inp type="number" value={f.savingsBalance} onChange={v=>upd("savingsBalance",v)} placeholder="e.g. 17585.63"/></div>
          <div><Lbl>Total CC Balance ($)</Lbl><Inp type="number" value={f.ccBalance} onChange={v=>upd("ccBalance",v)} placeholder="e.g. 19078.39"/></div>
          <div><Lbl>LOC Balance ($)</Lbl><Inp type="number" value={f.locBalance} onChange={v=>upd("locBalance",v)} placeholder="e.g. 56691.23"/></div>
          <div><Lbl>Payment Plan Balance ($)</Lbl><Inp type="number" value={f.paymentPlanBalance} onChange={v=>upd("paymentPlanBalance",v)} placeholder="Payments chq committed"/></div>
          <div style={{gridColumn:"1/-1"}}><Lbl>Notes</Lbl><Inp value={f.notes} onChange={v=>upd("notes",v)} placeholder="Context for this period…"/></div>
        </div>
        {hasCalc&&(
          <div style={{background:T.raised,borderRadius:8,padding:"10px 12px",display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:8}}>
            {[
              ["Sav Δ",savD],["CC Δ",ccD?-ccD:null],["LOC Δ",locD?-locD:null],["True Wealth Δ",trueW]
            ].map(([l,v])=>(
              <div key={l}>
                <div style={{fontSize:9,color:T.muted,fontFamily:"'Geist',sans-serif",textTransform:"uppercase",marginBottom:2}}>{l}</div>
                <div style={{fontSize:12,fontFamily:"'Geist',sans-serif",color:col(v),fontWeight:600}}>{v!=null?fmtM(v,true):"—"}</div>
              </div>
            ))}
          </div>
        )}
        {!hasCalc&&priorPd?.savingsBalance==null&&(
          <div style={{fontSize:10,color:T.muted,fontFamily:"'Geist',sans-serif",background:T.raised,borderRadius:6,padding:"8px 10px"}}>
            Deltas calculated automatically once prior period has balances entered.
          </div>
        )}
        <div style={{fontSize:9,color:T.muted,fontFamily:"'Geist',sans-serif"}}>
          True Wealth Δ = Sav Δ − CC increase − LOC increase · CC/LOC going up = more debt = wealth decrease
        </div>
        <div style={{display:"flex",gap:8,justifyContent:"flex-end",paddingTop:4}}>
          <Btn v="g" onClick={onClose}>Cancel</Btn>
          <Btn onClick={()=>onSave({savingsBalance:n(f.savingsBalance),ccBalance:n(f.ccBalance),locBalance:n(f.locBalance),paymentPlanBalance:n(f.paymentPlanBalance),notes:f.notes})}>Save</Btn>
        </div>
      </Card>
    </div>
  );
}

// ── Expense Modal ──────────────────────────────────────────────────────────
function ExpModal({exp,onSave,onClose}) {
  const [f,setF]=useState(exp||{id:uid(),name:"",amount:"",freq:"biweekly",dom:"",cat:"Housing",active:true,start:"",end:""});
  const upd=(k,v)=>setF(p=>({...p,[k]:v}));
  const valid=f.name&&+f.amount>0;
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(27,27,43,0.40)",backdropFilter:"blur(4px)",WebkitBackdropFilter:"blur(4px)",zIndex:200,display:"flex",alignItems:"flex-end",justifyContent:"center"}}>
      <Card className="sheet" style={{maxWidth:400,padding:18,display:"flex",flexDirection:"column",gap:12}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <h3 style={{fontFamily:"'Outfit',sans-serif",fontSize:15,fontWeight:700,color:T.text}}>{exp?"Edit":"Add"} Expense</h3>
          <Btn v="g" sm onClick={onClose}>✕</Btn>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          <div style={{gridColumn:"1/-1"}}><Lbl>Name</Lbl><Inp value={f.name} onChange={v=>upd("name",v)} placeholder="Expense name"/></div>
          <div><Lbl>Amount ($)</Lbl><Inp type="number" value={f.amount} onChange={v=>upd("amount",v)} placeholder="0.00"/></div>
          <div><Lbl>Category</Lbl><Sel value={f.cat} onChange={v=>upd("cat",v)} options={CATS}/></div>
          <div><Lbl>Frequency</Lbl><Sel value={f.freq} onChange={v=>upd("freq",v)} options={[{v:"biweekly",l:"Biweekly"},{v:"monthly",l:"Monthly"}]}/></div>
          {f.freq==="monthly"&&<div><Lbl>Day of Month</Lbl><Inp type="number" value={f.dom} onChange={v=>upd("dom",+v)} placeholder="1–31"/></div>}
          <div><Lbl>Start Date (opt)</Lbl><Inp type="date" value={f.start} onChange={v=>upd("start",v)}/></div>
          <div><Lbl>End Date (opt)</Lbl><Inp type="date" value={f.end} onChange={v=>upd("end",v)}/></div>
        </div>
        <label style={{display:"flex",alignItems:"center",gap:8,fontSize:12,color:T.muted,fontFamily:"'Geist',sans-serif",cursor:"pointer"}}>
          <input type="checkbox" checked={f.active} onChange={e=>upd("active",e.target.checked)}/> Active
        </label>
        <div style={{display:"flex",gap:8,justifyContent:"flex-end",paddingTop:4}}>
          <Btn v="g" onClick={onClose}>Cancel</Btn>
          <Btn onClick={()=>{if(valid)onSave({...f,amount:+f.amount,dom:f.dom?+f.dom:null})}} disabled={!valid}>Save</Btn>
        </div>
      </Card>
    </div>
  );
}

// ── One-Time Modal ─────────────────────────────────────────────────────────
function OneTimeModal({item,periods,onSave,onClose}) {
  const now=new Date();
  const futurePeriods=periods.filter(p=>D(p.end)>=now).slice(0,30);
  const pastPeriods=[...periods.filter(p=>D(p.end)<now)].reverse();
  const itemIsPast=!!(item?.periodKey&&pastPeriods.some(p=>p.key===item.periodKey));
  const [showPast,setShowPast]=useState(itemIsPast);
  const [f,setF]=useState(item||{id:uid(),name:"",amount:"",type:"expense",cat:"Other",source:"Savings",periodKey:futurePeriods[0]?.key||"",note:""});
  const upd=(k,v)=>setF(p=>({...p,[k]:v}));
  const togglePast=()=>{
    const next=!showPast; setShowPast(next);
    const list=next?pastPeriods:futurePeriods;
    if(list.length) upd("periodKey",list[0].key);
  };
  const pLabel=p=>`${p.label}, ${p.start.slice(0,4)}`;
  const visiblePeriods=showPast?pastPeriods:futurePeriods;
  const valid=f.name&&+f.amount>0&&f.periodKey;
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(27,27,43,0.40)",backdropFilter:"blur(4px)",WebkitBackdropFilter:"blur(4px)",zIndex:200,display:"flex",alignItems:"flex-end",justifyContent:"center"}}>
      <Card className="sheet" style={{maxWidth:420,padding:18,display:"flex",flexDirection:"column",gap:12}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <h3 style={{fontFamily:"'Outfit',sans-serif",fontSize:15,fontWeight:700,color:T.text}}>{item?"Edit":"Add"} One-Time Item</h3>
          <Btn v="g" sm onClick={onClose}>✕</Btn>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          <div style={{gridColumn:"1/-1"}}><Lbl>Name</Lbl><Inp value={f.name} onChange={v=>upd("name",v)} placeholder="e.g. Spain vacation, Tax refund, Car repair"/></div>
          <div>
            <Lbl>Type</Lbl>
            <div style={{display:"flex",gap:6}}>
              {["expense","income"].map(t=>(
                <button key={t} onClick={()=>upd("type",t)} style={{flex:1,padding:"6px 0",borderRadius:6,border:`1px solid ${f.type===t?(t==="income"?T.green:T.red):T.border}`,background:"none",color:f.type===t?(t==="income"?T.green:T.red):T.muted,cursor:"pointer",fontFamily:"'Geist',sans-serif",fontSize:11}}>
                  {t==="income"?"💰 Income":"💸 Expense"}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Lbl>Funded from</Lbl>
            <div style={{display:"flex",gap:6}}>
              {[["Savings","💰",T.green],["CC","💳",T.yellow],["LOC","🏦",T.red]].map(([s,icon,col])=>(
                <button key={s} onClick={()=>upd("source",s)} style={{flex:1,padding:"6px 0",borderRadius:6,border:`1px solid ${f.source===s?col:T.border}`,background:f.source===s?col+"15":"none",color:f.source===s?col:T.muted,cursor:"pointer",fontFamily:"'Geist',sans-serif",fontSize:11}}>
                  {icon} {s}
                </button>
              ))}
            </div>
          </div>
          <div><Lbl>Amount ($)</Lbl><Inp type="number" value={f.amount} onChange={v=>upd("amount",v)} placeholder="0.00"/></div>
          <div style={{gridColumn:"1/-1"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
              <Lbl style={{margin:0}}>Period</Lbl>
              <label style={{display:"flex",alignItems:"center",gap:5,cursor:"pointer",fontSize:11,color:T.muted,fontFamily:"'Geist',sans-serif"}}>
                <input type="checkbox" checked={showPast} onChange={togglePast} style={{cursor:"pointer"}}/>
                historical
              </label>
            </div>
            <Sel value={f.periodKey} onChange={v=>upd("periodKey",v)}
              options={visiblePeriods.map(p=>({v:p.key,l:pLabel(p)}))}/>
            {showPast&&<div style={{fontSize:10,color:T.muted,fontFamily:"'Geist',sans-serif",marginTop:3}}>Showing past periods — most recent first</div>}
          </div>
          <div><Lbl>Category</Lbl><Sel value={f.cat} onChange={v=>upd("cat",v)} options={CATS}/></div>
          <div><Lbl>Note (opt)</Lbl><Inp value={f.note} onChange={v=>upd("note",v)} placeholder="Optional context"/></div>
        </div>
        <div style={{display:"flex",gap:8,justifyContent:"flex-end",paddingTop:4}}>
          <Btn v="g" onClick={onClose}>Cancel</Btn>
          <Btn onClick={()=>{if(valid)onSave({...f,amount:+f.amount})}} disabled={!valid}>Save</Btn>
        </div>
      </Card>
    </div>
  );
}

// ── Expenses Tab ───────────────────────────────────────────────────────────
function ExpensesTab({state,update,periods}) {
  const [modal,setModal]=useState(null);
  const [otModal,setOtModal]=useState(null);
  const [pastOtCollapsed,setPastOtCollapsed]=useState(true);
  const [filt,setFilt]=useState("All");
  const [srch,setSrch]=useState("");
  const exps=state.expenses;
  const ot=state.oneTime||[];
  const filtered=exps.filter(e=>(filt==="All"||e.cat===filt)&&(!srch||e.name.toLowerCase().includes(srch.toLowerCase())));
  const saveExp=e=>{ const exists=exps.find(x=>x.id===e.id); update({expenses:exists?exps.map(x=>x.id===e.id?e:x):[...exps,e]}); setModal(null); };
  const delExp=id=>update({expenses:exps.filter(e=>e.id!==id)});
  const togAct=id=>update({expenses:exps.map(e=>e.id===id?{...e,active:!e.active}:e)});
  // Totals must agree with the projections, which use expAmt — that means
  // honouring start/end dates, not just the active flag (this used to ignore them).
  const curP=periods.find(p=>D(p.start)<=new Date()&&D(p.end)>=new Date());
  const outOfWindow=e=>!!curP&&!!((e.start&&D(e.start)>D(curP.end))||(e.end&&D(e.end)<D(curP.start)));
  const nominal=e=>e.freq==="monthly"?e.amount/2:e.amount;
  const totPer=exps.reduce((s,e)=>s+(curP?expAmt(e,curP):(e.active?nominal(e):0)),0);
  const nActive=exps.filter(e=>e.active&&!outOfWindow(e)).length;
  const saveOt=o=>{ const exists=ot.find(x=>x.id===o.id); update({oneTime:exists?ot.map(x=>x.id===o.id?o:x):[...ot,o]}); setOtModal(null); };
  const delOt=id=>update({oneTime:ot.filter(o=>o.id!==id)});
  return (
    <div style={{display:"flex",flexDirection:"column",gap:12}}>
      {modal&&<ExpModal exp={modal==="add"?null:modal} onSave={saveExp} onClose={()=>setModal(null)}/>}
      {otModal&&<OneTimeModal item={otModal==="add"?null:otModal} periods={periods} onSave={saveOt} onClose={()=>setOtModal(null)}/>}
      <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
        <Inp value={srch} onChange={setSrch} placeholder="Search…" style={{width:160}}/>
        <Sel value={filt} onChange={setFilt} options={["All",...CATS]} style={{width:140}}/>
        <div style={{marginLeft:"auto",display:"flex",gap:8,alignItems:"center"}}>
          <Btn onClick={()=>setModal("add")}>+ Add</Btn>
        </div>
      </div>
      <Card style={{padding:0,overflow:"auto"}}>
        <table style={{width:"100%",borderCollapse:"collapse",minWidth:620}}>
          <thead><tr style={{borderBottom:`1px solid ${T.border}`,background:T.raised}}>
            {["Name","Per Period","Frequency","Category","Active",""].map((h,i,arr)=><th key={h||"act"} style={i===arr.length-1?{...sx.th,position:"sticky",right:0,background:T.raised}:sx.th}>{h}</th>)}
          </tr></thead>
          <tbody>{filtered.map(e=>(
            <tr key={e.id} style={{borderBottom:`1px solid ${T.border}22`,opacity:e.active?1:0.45}}>
              <td style={{...sx.td,color:T.text,maxWidth:180}}>
                {e.name}
                {(e.start||e.end)&&<span style={{fontSize:9,color:T.muted,marginLeft:5}}>{e.start?`from ${e.start}`:""}{e.end?` to ${e.end}`:""}</span>}
              </td>
              <td style={sx.td}>
                <Num c={outOfWindow(e)?T.muted:T.yellow}>{fmtM(nominal(e))}</Num>
                {outOfWindow(e)&&<div style={{fontSize:9,color:T.muted,fontFamily:"'Geist',sans-serif"}}>not this period</div>}
              </td>
              <td style={{...sx.td,color:T.muted}}>{e.freq==="monthly"?`Mo. d${e.dom||"?"}`:"Biweekly"}</td>
              <td style={sx.td}><span style={{fontSize:10,padding:"2px 7px",borderRadius:20,background:T.raised,color:T.muted,border:`1px solid ${T.border}`}}>{e.cat}</span></td>
              <td style={sx.td}>
                <button onClick={()=>togAct(e.id)} style={{background:"none",border:"none",cursor:"pointer",color:e.active?T.accent:T.muted,fontSize:11,fontFamily:"'Geist',sans-serif"}}>
                  {e.active?"● on":"○ off"}
                </button>
              </td>
              <td style={{...sx.td,whiteSpace:"nowrap",position:"sticky",right:0,background:T.surf,boxShadow:"-10px 0 10px -10px rgba(27,27,43,0.18)"}}>
                <Btn sm v="g" onClick={()=>setModal(e)}>Edit</Btn>{" "}
                <Btn sm v="d" onClick={()=>delExp(e.id)}>Del</Btn>
              </td>
            </tr>
          ))}
          {!filtered.length&&<tr><td colSpan={6} style={{padding:32,textAlign:"center",color:T.muted,fontSize:12,fontFamily:"'Geist',sans-serif"}}>{!exps.length?'No budget items yet — click + Add to create one.':'No matches.'}</td></tr>}
          </tbody>
          {exps.length>0&&<tfoot>
            <tr style={{borderTop:`1px solid ${T.border}`,background:T.raised}}>
              <td style={{...sx.td,color:T.muted}}>Total ({nActive} active{curP?" this period":""})</td>
              <td style={sx.td}><Num c={T.red} style={{fontWeight:600}}>{fmtM(totPer)}</Num></td>
              <td colSpan={4} style={{...sx.td,color:T.muted}}>{fmtM(totPer*2)}/mo · {fmtM(totPer*26)}/yr</td>
            </tr>
          </tfoot>}
        </table>
      </Card>

      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:6}}>
        <div>
          <h3 style={{fontFamily:"'Outfit',sans-serif",fontSize:14,fontWeight:700,color:T.text}}>One-Time Items</h3>
          <p style={{fontSize:11,color:T.muted,fontFamily:"'Geist',sans-serif",marginTop:2}}>Period-specific deviations — vacation, tax refund, bonus, repairs, etc.</p>
        </div>
        <Btn onClick={()=>setOtModal("add")}>+ Add</Btn>
      </div>
      {(()=>{
        const now=new Date();
        const upcomingOt=[...ot].filter(o=>{const p=periods.find(x=>x.key===o.periodKey);return !p||D(p.end)>=now;}).sort((a,b)=>a.periodKey.localeCompare(b.periodKey));
        const pastOt=[...ot].filter(o=>{const p=periods.find(x=>x.key===o.periodKey);return p&&D(p.end)<now;}).sort((a,b)=>b.periodKey.localeCompare(a.periodKey));
        const OtRow=({o})=>{const pLabel=periods.find(p=>p.key===o.periodKey)?.label||o.periodKey;return(
          <tr key={o.id} style={{borderBottom:`1px solid ${T.border}22`}}>
            <td style={{...sx.td,color:T.muted,whiteSpace:"nowrap"}}>{pLabel}</td>
            <td style={sx.td}><span style={{fontSize:10,padding:"2px 8px",borderRadius:20,background:o.type==="income"?"rgba(22,163,74,0.1)":"rgba(220,38,38,0.1)",color:o.type==="income"?T.green:T.red,border:`1px solid ${o.type==="income"?T.green:T.red}44`}}>{o.type==="income"?"💰 Income":"💸 Expense"}</span></td>
            <td style={sx.td}><span style={{fontSize:10,padding:"2px 6px",borderRadius:20,background:T.raised,color:o.source==="CC"?T.yellow:o.source==="LOC"?T.red:T.green,border:`1px solid ${o.source==="CC"?T.yellow:o.source==="LOC"?T.red:T.green}44`}}>{o.source||"Savings"}</span></td>
            <td style={{...sx.td,color:T.text}}>{o.name}</td>
            <td style={sx.td}><Num c={o.type==="income"?T.green:T.red}>{fmtM(o.amount,true)}</Num></td>
            <td style={sx.td}><span style={{fontSize:10,padding:"2px 7px",borderRadius:20,background:T.raised,color:T.muted,border:`1px solid ${T.border}`}}>{o.cat}</span></td>
            <td style={{...sx.td,color:T.muted,fontSize:11,maxWidth:160}}>{o.note||"—"}</td>
            <td style={{...sx.td,whiteSpace:"nowrap",position:"sticky",right:0,background:T.surf,boxShadow:"-10px 0 10px -10px rgba(27,27,43,0.18)"}}><Btn sm v="g" onClick={()=>setOtModal(o)}>Edit</Btn>{" "}<Btn sm v="d" onClick={()=>delOt(o.id)}>Del</Btn></td>
          </tr>
        );};
        const hdr=<thead><tr style={{borderBottom:`1px solid ${T.border}`,background:T.raised}}>
          {["Period","Type","Source","Name","Amount","Category","Note",""].map((h,i,arr)=><th key={h||"act"} style={i===arr.length-1?{...sx.th,position:"sticky",right:0,background:T.raised}:sx.th}>{h}</th>)}
        </tr></thead>;
        return (<>
          <Card style={{padding:0,overflow:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse",minWidth:760}}>
              {hdr}
              <tbody>
                {upcomingOt.map(o=><OtRow key={o.id} o={o}/>)}
                {!upcomingOt.length&&<tr><td colSpan={8} style={{padding:24,textAlign:"center",color:T.muted,fontSize:12,fontFamily:"'Geist',sans-serif"}}>No upcoming one-time items. Click + Add above.</td></tr>}
              </tbody>
            </table>
          </Card>
          {pastOt.length>0&&(
            <div>
              <button onClick={()=>setPastOtCollapsed(p=>!p)} style={{display:"flex",alignItems:"center",gap:6,background:"none",border:"none",cursor:"pointer",padding:"6px 0",width:"100%"}}>
                <span style={{fontSize:11,color:T.muted,fontFamily:"'Geist',sans-serif"}}>{pastOtCollapsed?"▶":"▼"}</span>
                <span style={{fontSize:12,color:T.muted,fontFamily:"'Geist',sans-serif",fontWeight:500}}>Past items ({pastOt.length})</span>
                <span style={{fontSize:10,color:T.muted,fontFamily:"'Geist',sans-serif",marginLeft:4}}>— completed periods</span>
              </button>
              {!pastOtCollapsed&&(
                <Card style={{padding:0,overflow:"auto",opacity:0.75}}>
                  <table style={{width:"100%",borderCollapse:"collapse",minWidth:760}}>
                    {hdr}
                    <tbody>{pastOt.map(o=><OtRow key={o.id} o={o}/>)}</tbody>
                  </table>
                </Card>
              )}
            </div>
          )}
        </>);
      })()}
    </div>
  );
}

// ── AI Proj Cell ───────────────────────────────────────────────────────────
function AiProjCell({periodKey,value,onChange}) {
  const [editing,setEditing]=useState(false);
  const [draft,setDraft]=useState("");
  const start=()=>{ setDraft(value!=null?String(value):""); setEditing(true); };
  const commit=()=>{ const n=draft.trim(); onChange(n===""?null:+n); setEditing(false); };
  if (editing) return (
    <div style={{display:"flex",gap:4,alignItems:"center"}}>
      <input autoFocus type="number" value={draft} onChange={e=>setDraft(e.target.value)}
        onKeyDown={e=>{if(e.key==="Enter")commit();if(e.key==="Escape")setEditing(false);}}
        onBlur={commit}
        style={{...sx.input,width:90,padding:"2px 6px",fontSize:11,color:T.yellow,border:`1px solid ${T.yellow}88`}}/>
    </div>
  );
  return (
    <div style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer"}} onClick={start}>
      {value!=null
        ? <><span style={{fontFamily:"'Geist',sans-serif",fontSize:12,color:T.yellow,fontWeight:600}}>{fmtM(value)}</span>
            <button onClick={e=>{e.stopPropagation();onChange(null);}} style={{background:"none",border:"none",cursor:"pointer",color:T.muted,fontSize:10,padding:0,lineHeight:1}}>✕</button></>
        : <span style={{color:T.muted,fontSize:11,fontFamily:"'Geist',sans-serif"}}>— click to set</span>
      }
    </div>
  );
}

// ── Note tooltip ───────────────────────────────────────────────────────────
// Friendly styled tooltip (never the native title= bubble). Hover previews;
// click pins it open (click anywhere else closes). Fixed-position so the
// table card's overflow:auto can't clip it.
function NoteTip({tip,onClose}) {
  useEffect(()=>{
    if(!tip?.pinned) return;
    const h=()=>onClose();
    window.addEventListener("click",h);
    return ()=>window.removeEventListener("click",h);
  },[tip,onClose]);
  if(!tip) return null;
  const arrow=<div style={{width:10,height:10,background:T.text,transform:"rotate(45deg)",borderRadius:2,margin:tip.below?"0 auto -5px":"-5px auto 0",position:"relative",zIndex:1}}/>;
  return (
    <div onClick={e=>e.stopPropagation()} style={{position:"fixed",left:Math.min(Math.max(tip.x,180),(window.innerWidth||1200)-180),top:tip.y,transform:tip.below?"translate(-50%,0)":"translate(-50%,-100%)",zIndex:300,pointerEvents:tip.pinned?"auto":"none"}}>
      {tip.below&&arrow}
      <div style={{maxWidth:340,background:T.text,color:"#fff",borderRadius:12,padding:"10px 14px",fontSize:12,lineHeight:1.6,fontFamily:"'Geist',sans-serif",boxShadow:"0 10px 28px -6px rgba(27,27,43,0.45)",whiteSpace:"pre-wrap"}}>
        {tip.text}
        {tip.pinned&&<div style={{fontSize:10,color:"rgba(255,255,255,0.55)",marginTop:6}}>Click anywhere to close</div>}
      </div>
      {!tip.below&&arrow}
    </div>
  );
}

// ── Summary Tab ────────────────────────────────────────────────────────────
function SummaryTab({state,update,periods,metrics}) {
  const [editPeriodKey,setEditPeriodKey]=useState(null);
  const [view,setView]=useState("history");
  const [chartOff,setChartOff]=useState(0); // chart window shift, in periods (0 = centred on today)
  const [noteTip,setNoteTip]=useState(null);
  const noteTipHandlers=(text)=>!text?{}:({
    onMouseEnter:e=>{ const r=e.currentTarget.getBoundingClientRect(); const below=r.top<130; setNoteTip(t=>t?.pinned?t:{text,below,x:r.left+r.width/2,y:below?r.bottom+8:r.top-8,pinned:false}); },
    onMouseLeave:()=>setNoteTip(t=>t?.pinned?t:null),
    onClick:e=>{ e.stopPropagation(); const r=e.currentTarget.getBoundingClientRect(); const below=r.top<130; setNoteTip({text,below,x:r.left+r.width/2,y:below?r.bottom+8:r.top-8,pinned:true}); },
  });
  const now=new Date();
  const cur=periods.find(p=>D(p.start)<=now&&D(p.end)>=now);
  const past=periods.filter(p=>D(p.end)<now);
  const future=periods.filter(p=>D(p.start)>now);
  const cm=cur?metrics[cur.key]:null;
  const curPd=cur?(state.periodData[cur.key]||{}):null;
  const ytdPs=past.filter(p=>D(p.start).getFullYear()===now.getFullYear());
  const ytdAct=ytdPs.reduce((s,p)=>{const m=metrics[p.key];return s+(m?.act??m?.bSav??0);},0);
  const ytdBud=ytdPs.reduce((s,p)=>s+(metrics[p.key]?.bSav||0),0);
  const chartData=([...past.slice(-11),cur].filter(Boolean)).map(p=>{const m=metrics[p.key];return{name:p.label,budgeted:Math.round(m?.bSav||0),actual:m?.act!=null?Math.round(m.act):null};});
  const recentBalance=useMemo(()=>[...[cur,...[...past].reverse()].filter(Boolean)].map(p=>state.periodData[p.key]?.savingsBalance).find(b=>b!=null)??0,[state.periodData,cur,past]);
  const projData=useMemo(()=>{
    let bal=recentBalance+(cur?(metrics[cur.key]?.bSavSavings||0):0);
    return future.slice(0,26).map(p=>{
      bal+=(metrics[p.key]?.bSavSavings||0);
      return{wealthDelta:Math.round(metrics[p.key]?.bSav||0),projBalance:Math.round(bal)};
    });
  },[metrics,recentBalance,cur,future,state.oneTime]);
  const savePeriodData=(pk,data)=>{
    const pd={...(state.periodData||{})};
    pd[pk]={...(pd[pk]||{}),...data};
    update({periodData:pd});
    setEditPeriodKey(null);
  };
  const histRows=[...past].reverse().slice(0,24);
  const getPriorPd=(pk)=>{
    const idx=periods.findIndex(p=>p.key===pk);
    const priorWithBal=periods.slice(0,idx).reverse().find(p=>state.periodData[p.key]?.savingsBalance!=null);
    return priorWithBal?state.periodData[priorWithBal.key]:null;
  };
  const editPd=editPeriodKey?(state.periodData[editPeriodKey]||null):null;
  const editLabel=editPeriodKey?periods.find(p=>p.key===editPeriodKey)?.label:null;

  return (
    <div style={{display:"flex",flexDirection:"column",gap:14}}>
      <NoteTip tip={noteTip} onClose={()=>setNoteTip(null)}/>
      {editPeriodKey&&<PeriodEditModal pk={editPeriodKey} label={editLabel} pd={editPd} priorPd={getPriorPd(editPeriodKey)} onSave={d=>savePeriodData(editPeriodKey,d)} onClose={()=>setEditPeriodKey(null)}/>}
      {(()=>{
        // Projected balance at end of current period
        const endOfPeriodBal=recentBalance+(cur?(metrics[cur.key]?.bSavSavings||0):0);
        // Projected balance in ~90 days (6 periods out)
        const in90Days=addDays(now,90);
        const periodsTo90=[cur,...future].filter(Boolean).filter(p=>D(p.end)<=in90Days);
        const bal90=recentBalance+periodsTo90.reduce((s,p)=>s+(metrics[p.key]?.bSavSavings||0),0);
        // Projected balance in ~6 months (13 periods out)
        const in6mo=addDays(now,183);
        const periodsTo6=[cur,...future].filter(Boolean).filter(p=>D(p.end)<=in6mo);
        const bal6=recentBalance+periodsTo6.reduce((s,p)=>s+(metrics[p.key]?.bSavSavings||0),0);
        // AI 6-month outlook: the furthest AI-projected balance within the horizon
        const aiP=state.aiProjections||{};
        const ai6cand=[cur,...future].filter(Boolean).filter(p=>D(p.end)<=in6mo&&aiP[p.key]!=null);
        const ai6=ai6cand.length?{v:aiP[ai6cand[ai6cand.length-1].key],label:ai6cand[ai6cand.length-1].label}:null;
        const cards=[
          {l:"Current Period",v:cur?.label||"—",s:`Income: ${fmtM(cm?.inc)}`,c:T.text,big:true},
          {l:"Budgeted Savings",v:fmtM(cm?.bSav),s:"This period",c:cm?.bSav>=0?T.accent:T.red},
          {l:"End of Period",v:fmtM(endOfPeriodBal),s:"Projected savings balance",c:endOfPeriodBal>=recentBalance?T.green:T.red},
          {l:"In 90 Days",v:fmtM(bal90),s:`~${periodsTo90.length} periods if on budget`,c:bal90>=recentBalance?T.green:T.red},
          {l:"In 6 Months",v:fmtM(bal6),s:`${periodsTo6.length} periods if on budget`,c:bal6>=recentBalance?T.green:T.red},
          {l:"AI 6-Month Outlook",v:ai6?fmtM(ai6.v):"—",s:ai6?`✦ AI projection · ${ai6.label}`:"Ask FamCFO for a projection",c:ai6?T.yellow:T.muted},
        ];
        // Line chart: 12-month window centred on today, shiftable 6 months at a time
        const aiProj=state.aiProjections||{};
        const HALF=13; // 13 biweekly periods ≈ 6 months
        const projByKey={};
        if(cur) projByKey[cur.key]=Math.round(endOfPeriodBal);
        let runBal=endOfPeriodBal;
        future.forEach(p=>{ runBal+=(metrics[p.key]?.bSavSavings||0); projByKey[p.key]=Math.round(runBal); });
        const centerIdx=cur?periods.findIndex(p=>p.key===cur.key):past.length;
        const winStart=Math.max(0,centerIdx-HALF+chartOff);
        const winEnd=Math.min(periods.length,centerIdx+HALF+1+chartOff);
        const winPoints=periods.slice(winStart,winEnd).map(p=>({
          name:p.label,
          actual:p.key===cur?.key?recentBalance:(D(p.end)<now?(metrics[p.key]?.savingsBalance??null):null),
          proj:projByKey[p.key]??null,
          aiProj:aiProj[p.key]??null,
        }));
        const canBack=winStart>0, canFwd=winEnd<periods.length;
        const winLabel=periods[winStart]&&periods[winEnd-1]
          ?`${MOS[D(periods[winStart].start).getMonth()]} ${D(periods[winStart].start).getFullYear()} – ${MOS[D(periods[winEnd-1].end).getMonth()]} ${D(periods[winEnd-1].end).getFullYear()}`:"";
        const hasAiProj=winPoints.some(p=>p.aiProj!=null);
        return (<>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(175px,1fr))",gap:10}}>
            {cards.map(c=><Card key={c.l} style={{padding:12}}>
              <Lbl>{c.l}</Lbl>
              <Num c={c.c} style={{fontSize:c.big?14:17,fontWeight:600,lineHeight:1.15,display:"block",marginBottom:3}}>{c.v}</Num>
              <div style={{fontSize:10,color:T.muted,fontFamily:"'Geist',sans-serif"}}>{c.s}</div>
            </Card>)}
          </div>
          <Card style={{padding:"12px 12px 8px"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:6,marginBottom:10}}>
              <Lbl style={{marginBottom:0}}>Savings Balance — 12-Month View</Lbl>
              <div style={{display:"flex",gap:6,alignItems:"center"}}>
                <Btn sm v="g" disabled={!canBack} onClick={()=>setChartOff(o=>o-HALF)}>‹ 6 mo</Btn>
                <span style={{fontSize:10,fontFamily:"'Geist',sans-serif",color:T.muted,whiteSpace:"nowrap"}}>{winLabel}</span>
                {chartOff!==0&&<Btn sm onClick={()=>setChartOff(0)}>Today</Btn>}
                <Btn sm v="g" disabled={!canFwd} onClick={()=>setChartOff(o=>o+HALF)}>6 mo ›</Btn>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <ComposedChart data={winPoints} margin={{top:4,right:8,bottom:20,left:8}}>
                <CartesianGrid strokeDasharray="3 3" stroke={T.border} vertical={false}/>
                <XAxis dataKey="name" tick={{fill:T.muted,fontSize:8,fontFamily:"'Geist',sans-serif"}} tickLine={false} axisLine={false} angle={-35} textAnchor="end" height={48} minTickGap={10}/>
                <YAxis tick={{fill:T.muted,fontSize:9,fontFamily:"'Geist',sans-serif"}} tickLine={false} axisLine={false} tickFormatter={v=>"$"+Math.round(v/1000)+"k"} width={40}/>
                <Tooltip contentStyle={{background:T.raised,border:`1px solid ${T.border}`,borderRadius:8,fontFamily:"'Geist',sans-serif",fontSize:11}} formatter={(v,n)=>[v!=null?fmtM(v):"—",n]}/>
                <ReferenceLine y={0} stroke={T.border} strokeWidth={1}/>
                <Line type="monotone" dataKey="actual" name="Actual Balance" stroke={T.green} strokeWidth={2} dot={{r:3,fill:T.green}} connectNulls={false}/>
                <Line type="monotone" dataKey="proj" name="Projected Balance" stroke={T.accent} strokeWidth={2} strokeDasharray="5 3" dot={{r:3,fill:T.accent}} connectNulls={false}/>
                {hasAiProj&&<Line type="monotone" dataKey="aiProj" name="AI Projected" stroke={T.yellow} strokeWidth={2} strokeDasharray="3 3" dot={{r:3,fill:T.yellow}} connectNulls={false}/>}
              </ComposedChart>
            </ResponsiveContainer>
            <div style={{display:"flex",gap:16,justifyContent:"center",marginTop:4}}>
              {[["Actual Balance",T.green,""],["Projected (if on budget)",T.accent,"5 3"],...(hasAiProj?[["AI Projected",T.yellow,"3 3"]]:[])] .map(([l,c,d])=>(
                <div key={l} style={{display:"flex",alignItems:"center",gap:5,fontSize:10,color:T.muted,fontFamily:"'Geist',sans-serif"}}>
                  <svg width="24" height="10"><line x1="0" y1="5" x2="24" y2="5" stroke={c} strokeWidth="2" strokeDasharray={d}/></svg>
                  {l}
                </div>
              ))}
            </div>
          </Card>
        </>);
      })()}
      <div style={{display:"flex",gap:2,background:T.raised,borderRadius:8,padding:2,alignSelf:"flex-start",border:`1px solid ${T.border}`}}>
        {["history","projection"].map(v=>(
          <button key={v} onClick={()=>setView(v)} style={{padding:"5px 14px",borderRadius:6,border:"none",cursor:"pointer",fontSize:11,fontFamily:"'Geist',sans-serif",background:view===v?T.surf:"transparent",color:view===v?T.text:T.muted,fontWeight:view===v?600:400}}>
            {v==="history"?"History":"12-Mo Projection"}
          </button>
        ))}
      </div>
      {view==="history"&&<Card style={{padding:0,overflow:"auto"}}>
        <table style={{width:"100%",borderCollapse:"collapse",minWidth:960}}>
          <thead><tr style={{borderBottom:`1px solid ${T.border}`,background:T.raised}}>
            {/* Edit column is pinned (sticky right) so it never scrolls out of view or clips against the card edge */}
            {["Period","Sav. Balance","CC Balance","LOC Balance","PP Balance","Sav. Δ","CC Δ","True Wealth Δ","vs Budget","Notes",""].map((h,i,arr)=><th key={h||"edit"} style={i===arr.length-1?{...sx.th,position:"sticky",right:0,background:T.raised}:sx.th}>{h}</th>)}
          </tr></thead>
          <tbody>{histRows.flatMap((p,i)=>{
            const m=metrics[p.key];
            const nextP=histRows[i+1];
            const yearChange=nextP&&D(p.start).getFullYear()!==D(nextP.start).getFullYear();
            const yr=yearChange?D(nextP.start).getFullYear():null;
            const col=v=>v==null?T.muted:v>=0?T.green:T.red;
            return [
              <tr key={p.key} style={{borderBottom:`1px solid ${T.border}22`}}>
                <td style={{...sx.td,color:T.text,fontWeight:400,whiteSpace:"nowrap"}}>
                  {p.label}
                  {m?.otItems?.length>0&&(
                    <span {...noteTipHandlers(m.otItems.map(o=>`${o.type==="income"?"[+]":"[-]"} ${o.name}: ${fmtM(o.amount)}${o.source&&o.source!=="Savings"?" ("+o.source+")":""}`).join("\n"))}
                      style={{marginLeft:5,cursor:"help",fontSize:9,fontFamily:"'Geist',sans-serif",background:T.yellow+"33",color:T.yellow,border:`1px solid ${T.yellow}55`,borderRadius:10,padding:"1px 5px"}}>
                      {m.otItems.length} OT
                    </span>
                  )}
                </td>
                <td style={sx.td}><Num c={m?.savingsBalance!=null?T.text:T.muted}>{m?.savingsBalance!=null?fmtM(m.savingsBalance):"—"}</Num></td>
                <td style={sx.td}><Num c={m?.ccBalance!=null?T.text:T.muted}>{m?.ccBalance!=null?fmtM(m.ccBalance):"—"}</Num></td>
                <td style={sx.td}><Num c={m?.locBalance!=null?T.text:T.muted}>{m?.locBalance!=null?fmtM(m.locBalance):"—"}</Num></td>
                <td style={sx.td}><Num c={m?.ppBalance!=null?T.text:T.muted}>{m?.ppBalance!=null?fmtM(m.ppBalance):"—"}</Num></td>
                <td style={sx.td}><Num c={col(m?.savingsDelta)}>{m?.savingsDelta!=null?fmtM(m.savingsDelta,true):"—"}</Num></td>
                <td style={sx.td}><Num c={m?.ccDelta!=null?col(-m.ccDelta):T.muted}>{m?.ccDelta!=null?fmtM(-m.ccDelta,true):"—"}</Num></td>
                <td style={{...sx.td,fontWeight:600}}><Num c={col(m?.trueWealthDelta)}>{m?.trueWealthDelta!=null?fmtM(m.trueWealthDelta,true):"—"}</Num></td>
                <td style={sx.td}><Num c={col(m?.delta)}>{m?.delta!=null?fmtM(m.delta,true):"—"}</Num></td>
                <td {...noteTipHandlers(m?.notes)} style={{...sx.td,color:T.muted,fontSize:10,maxWidth:160,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",cursor:m?.notes?"pointer":"default"}}>{m?.notes||"—"}</td>
                <td style={{...sx.td,paddingRight:16,whiteSpace:"nowrap",position:"sticky",right:0,background:T.surf,boxShadow:"-10px 0 10px -10px rgba(27,27,43,0.18)"}}><Btn sm v="g" onClick={()=>setEditPeriodKey(p.key)}>Edit</Btn></td>
              </tr>,
              yearChange&&<tr key={`yr-${yr}`}>
                <td colSpan={11} style={{padding:"5px 12px",textAlign:"center",background:T.accent+"10",borderTop:`2px solid ${T.accent}55`,borderBottom:`2px solid ${T.accent}55`,fontSize:10,color:T.accent,fontFamily:"'Geist',sans-serif",fontWeight:700,letterSpacing:"0.12em"}}>
                  &#8212;&#8212; {yr} &#8212;&#8212;
                </td>
              </tr>
            ].filter(Boolean);
          })}</tbody>
        </table>
      </Card>}
      {view==="projection"&&<Card style={{padding:0,overflow:"auto"}}>
        <table style={{width:"100%",borderCollapse:"collapse"}}>
          <thead><tr style={{borderBottom:`1px solid ${T.border}`,background:T.raised}}>
            {["Period","Income","Expenses","Proj. Wealth Δ","Proj. Sav. Balance"].map(h=><th key={h} style={sx.th}>{h}</th>)}
            <th style={{...sx.th,color:T.yellow,borderLeft:`2px solid ${T.yellow}44`}}>AI Proj. Balance ✦</th>
          </tr></thead>
          <tbody>
            {cur&&(()=>{
              const cm2=metrics[cur.key];
              const curProjBal=recentBalance+(cm2?.bSavSavings||0);
              const curActualBal=state.periodData[cur.key]?.savingsBalance;
              const aiVal=(state.aiProjections||{})[cur.key];
              return <tr style={{borderBottom:`2px solid ${T.accent}44`,background:T.accent+"08"}}>
                <td style={{...sx.td,color:T.accent,fontWeight:600,whiteSpace:"nowrap"}}>
                  {cur.label}&nbsp;<span style={{fontSize:9,background:T.accent,color:"#fff",borderRadius:10,padding:"1px 6px",fontFamily:"'Geist',sans-serif"}}>IN PROGRESS</span>
                </td>
                <td style={{...sx.td,color:T.muted}}>{fmtM(cm2?.inc)}</td>
                <td style={{...sx.td,color:T.red}}>{fmtM(cm2?.exp)}</td>
                <td style={{...sx.td,color:cm2?.bSav>=0?T.accent:T.red}}>{fmtM(cm2?.bSav)}</td>
                <td style={sx.td}>
                  <Num c={T.accent} style={{fontWeight:600}}>{fmtM(curProjBal)}</Num>
                  {curActualBal!=null&&<div style={{fontSize:9,color:T.muted,fontFamily:"'Geist',sans-serif"}}>actual: {fmtM(curActualBal)}</div>}
                  <div style={{fontSize:9,color:T.muted,fontFamily:"'Geist',sans-serif"}}>if on budget</div>
                </td>
                <td style={{...sx.td,borderLeft:`2px solid ${T.yellow}44`,background:aiVal!=null?T.yellow+"08":"transparent",paddingRight:16}}>
                  <AiProjCell periodKey={cur.key} value={aiVal} onChange={v=>{
                    const ap={...(state.aiProjections||{})};
                    if(v==null) delete ap[cur.key]; else ap[cur.key]=v;
                    update({aiProjections:ap});
                  }}/>
                </td>
              </tr>;
            })()}
            {future.slice(0,26).flatMap((p,i)=>{
            const m=metrics[p.key],pd=projData[i];
            const nextP=future[i+1];
            const yearChange=nextP&&D(p.start).getFullYear()!==D(nextP.start).getFullYear();
            const yr=yearChange?D(p.start).getFullYear():null;
            const aiVal=(state.aiProjections||{})[p.key];
            return [
              <tr key={p.key} style={{borderBottom:`1px solid ${T.border}22`}}>
                <td style={{...sx.td,color:T.text}}>
                  {p.label}
                  {m?.otItems?.length>0&&(
                    <span {...noteTipHandlers(m.otItems.map(o=>`${o.type==="income"?"[+]":"[-]"} ${o.name}: ${fmtM(o.amount)}${o.source&&o.source!=="Savings"?" ("+o.source+")":""}`).join("\n"))}
                      style={{marginLeft:5,cursor:"help",fontSize:9,fontFamily:"'Geist',sans-serif",background:T.yellow+"33",color:T.yellow,border:`1px solid ${T.yellow}55`,borderRadius:10,padding:"1px 5px"}}>
                      {m.otItems.length} OT
                    </span>
                  )}
                </td>
                <td style={{...sx.td,color:T.muted}}>{fmtM(m?.inc)}</td>
                <td style={{...sx.td,color:T.red}}>{fmtM(m?.exp)}</td>
                <td style={{...sx.td,color:m?.bSav>=0?T.accent:T.red}}>{fmtM(m?.bSav)}</td>
                <td style={{...sx.td,color:pd?.projBalance>=0?T.green:T.red,fontWeight:600}}>{fmtM(pd?.projBalance)}</td>
                <td style={{...sx.td,borderLeft:`2px solid ${T.yellow}44`,background:aiVal!=null?T.yellow+"08":"transparent",paddingRight:16}}>
                  <AiProjCell periodKey={p.key} value={aiVal} onChange={v=>{
                    const ap={...(state.aiProjections||{})};
                    if(v==null) delete ap[p.key]; else ap[p.key]=v;
                    update({aiProjections:ap});
                  }}/>
                </td>
              </tr>,
              yearChange&&<tr key={`yr-${yr}`}>
                <td colSpan={6} style={{padding:"5px 12px",textAlign:"center",background:T.accent+"10",borderTop:`2px solid ${T.accent}55`,borderBottom:`2px solid ${T.accent}55`,fontSize:10,color:T.accent,fontFamily:"'Geist',sans-serif",fontWeight:700,letterSpacing:"0.12em"}}>
                  &#8212;&#8212; END OF {yr} &#8212;&#8212;
                </td>
              </tr>
            ].filter(Boolean);
          })}</tbody>
        </table>
      </Card>}
    </div>
  );
}

// ── Upload Tab ─────────────────────────────────────────────────────────────

// Known accounts — pre-configured labels, include/trackSavings defaults
// full: full account number digits (for scientific notation fallback matching)
const KNOWN_ACCOUNTS = [
  {match:"5058300", full:"033725058300",        label:"Cash Chq ...5058300",       include:true,  trackSavings:false, note:"Day-to-day spending"},
  {match:"5062963", full:"028525062963",        label:"Spending Chq ...5062963",   include:true,  trackSavings:false, note:"Bills — always committed"},
  {match:"5110119", full:"033725110119",        label:"Savings ...5110119",         include:true,  trackSavings:true,  note:"Target savings account"},
  {match:"5156757", full:"099705156757",        label:"Kids Chq ...5156757",        include:false, trackSavings:false, note:"Kids account — exclude"},
  {match:"5156765", full:"099705156765",        label:"Kids Chq ...5156765",        include:false, trackSavings:false, note:"Kids account — exclude"},
  {match:"5183991", full:"016725183991",        label:"Invest. Property ...5183991",include:false, trackSavings:false, note:"Investment property — exclude"},
  {match:"2348",    full:"4646913201262348",    label:"Avion Prestige CC1 ...2348", include:true,  trackSavings:false, note:"High-interest — active paydown"},
  {match:"1609",    full:"4510262340031609",    label:"Silver Visa CC2 ...1609",    include:true,  trackSavings:false, note:"Regular card"},
  {match:"6470",    full:"5526124001826470",    label:"Sole Prop MC ...6470",       include:false, trackSavings:false, note:"Business — exclude"},
  {match:"1304",    full:"5526124502221304",    label:"GrowthSpan Biz MC ...1304",  include:false, trackSavings:false, note:"AI/SaaS business — exclude"},
  {match:"0103001", full:"0997087780103001",    label:"LOC #2 ...0103001",          include:false, trackSavings:false, note:"Pool/Ponderosa loan"},
  {match:"2752001", full:"01672626927520011",   label:"LOC #1 ...2752001",          include:false, trackSavings:false, note:"LOC #1"},
];
function lookupKnownAccount(acctNum) {
  const s=String(acctNum).trim();
  const n=s.replace(/[^0-9]/g,"");
  // Normal suffix match
  const direct=KNOWN_ACCOUNTS.find(k=>n.endsWith(k.match.replace(/[^0-9]/g,"")));
  if (direct) return direct;
  // Scientific notation fallback (e.g. 5.52612E+15 from Excel) — match by significant digits prefix
  if (/[Ee]/i.test(s)) {
    const sig=s.replace(/[Ee].*/i,"").replace(/[^0-9]/g,""); // mantissa digits only
    if (sig.length>=4) return KNOWN_ACCOUNTS.find(k=>(k.full||"").startsWith(sig))||null;
  }
  return null;
}

// CSV helpers
function parseDate(s) {
  if (!s) return null;
  const str=String(s).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str.slice(0,10);
  // M/D/YYYY or M/D/YY (RBC format) — m[1]=month, m[2]=day, m[3]=year
  const m=str.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/);
  if (m) { const y=m[3].length===2?"20"+m[3]:m[3]; return `${y}-${m[1].padStart(2,"0")}-${m[2].padStart(2,"0")}`; }
  return null;
}
function parseAmt(s) { if (!s||!String(s).trim()) return null; const n=parseFloat(String(s).replace(/[^0-9.\-]/g,"")); return isNaN(n)?null:n; }

// Check if this is an RBC-style multi-account CSV
function isRbcFormat(hdrs) {
  const h=hdrs.map(x=>String(x||"").toLowerCase().trim());
  return h.some(x=>x==="account number"||x==="account type") && h.some(x=>x.includes("description 1")||x==="description 1");
}

// Parse RBC multi-account CSV → map of acctNum → {meta, txns}
function parseRbcCsv(hdrs, dataRows, periods) {
  const h=hdrs.map(x=>String(x||"").toLowerCase().trim());
  const acctTypeI=h.findIndex(x=>x==="account type");
  const acctNumI=h.findIndex(x=>x==="account number");
  const dateI=h.findIndex(x=>x.includes("date"));
  const desc1I=h.findIndex(x=>x==="description 1");
  const desc2I=h.findIndex(x=>x==="description 2");
  const amtI=h.findIndex(x=>x==="cad$"||x==="cad");
  const map={};
  for (const r of dataRows) {
    const acctNum=String(r[acctNumI]||"").trim();
    if (!acctNum) continue;
    const date=parseDate(r[dateI]);
    if (!date) continue;
    const amt=parseAmt(r[amtI]);
    if (amt==null) continue;
    const d1=String(r[desc1I]||"").trim();
    const d2=String(r[desc2I]||"").trim();
    const description=[d1,d2].filter(Boolean).join(" ").trim();
    const periodKey=periods.find(p=>date>=p.start&&date<=p.end)?.key||null;
    if (!map[acctNum]) {
      map[acctNum]={acctType:String(r[acctTypeI]||"").trim(), acctNum, txns:[]};
    }
    map[acctNum].txns.push({id:uid(),date,description,amount:amt,periodKey});
  }
  return map;
}

// Generic single-account parse (existing format)
function detectCols(hdrs) {
  const h=hdrs.map(x=>String(x||"").toLowerCase().trim());
  return {
    dateI:h.findIndex(x=>x.includes("date")||x==="tran date"),
    descI:h.findIndex(x=>x.includes("desc")||x.includes("narrat")||x.includes("memo")||x==="details"),
    amtI:h.findIndex(x=>x==="amount"||x==="cad$"||x==="cad"||x==="transaction amount"),
    debI:h.findIndex(x=>x.includes("debit")||x.includes("withdrawal")),
    credI:h.findIndex(x=>x.includes("credit")||x.includes("deposit")),
  };
}
function csvToTxns(rows,cm,periods) {
  return rows.map(r=>{
    const date=parseDate(r[cm.dateI]);
    if (!date) return null;
    let amount;
    if (cm.amtI>=0) amount=parseAmt(r[cm.amtI]);
    else if (cm.debI>=0||cm.credI>=0) amount=(parseAmt(r[cm.credI])||0)-(Math.abs(parseAmt(r[cm.debI])||0));
    if (amount==null) return null;
    const description=String(r[cm.descI]||"").trim();
    const periodKey=periods.find(p=>date>=p.start&&date<=p.end)?.key||null;
    return {id:uid(),date,description,amount,periodKey};
  }).filter(Boolean);
}

function UploadTab({state,update,periods,onAnalyze}) {
  const [drag,setDrag]=useState(false);
  const [summary,setSummary]=useState(null);     // {uploadId,fileName,rows:[{label,added,skipped,from,to}],createdIds,total}
  const [pendingNew,setPendingNew]=useState([]); // unknown accounts awaiting one-time setup
  const [parsed,setParsed]=useState(null);       // non-RBC file awaiting column mapping
  const [cm,setCm]=useState({dateI:-1,descI:-1,amtI:-1,debI:-1,credI:-1});
  const [label,setLabel]=useState("");
  const [inc,setInc]=useState(true);
  const [sav,setSav]=useState(false);
  const ref=useRef();

  const rowFor=(lbl,fresh,parsedCount)=>{
    const dates=fresh.map(t=>t.date).sort();
    return {label:lbl,added:fresh.length,skipped:parsedCount-fresh.length,from:dates[0]||null,to:dates[dates.length-1]||null};
  };

  // Instant import: known accounts (saved settings, or KNOWN_ACCOUNTS defaults for
  // first-ever sight) merge with zero confirmation. Saved settings ALWAYS win —
  // uploads never touch label/include/trackSavings on an existing account.
  // Every imported txn is tagged with uploadId so "Undo this import" is exact.
  const importRbc=(fileName,accts)=>{
    const uploadId=uid();
    const accounts=[...state.accounts];
    const rows=[],createdIds=[],unknown=[];
    for(const a of accts){
      const idx=accounts.findIndex(x=>x.acctNum===a.acctNum);
      const known=lookupKnownAccount(a.acctNum);
      if(idx<0&&!known){ unknown.push(a); continue; }
      const txns=a.txns.map(t=>({...t,uploadId}));
      if(idx>=0){
        const existing=accounts[idx];
        const keys=new Set((existing.transactions||[]).map(t=>`${t.date}|${t.amount}|${t.description}`));
        const fresh=txns.filter(t=>!keys.has(`${t.date}|${t.amount}|${t.description}`));
        accounts[idx]={...existing,transactions:[...(existing.transactions||[]),...fresh].slice(0,3000)};
        rows.push(rowFor(existing.label||existing.name,fresh,a.txns.length));
      } else {
        const id=uid(); createdIds.push(id);
        accounts.push({id,name:a.acctNum,acctNum:a.acctNum,acctType:a.acctType,label:known.label,include:known.include,trackSavings:known.trackSavings,transactions:txns.slice(0,3000)});
        rows.push(rowFor(known.label,txns,a.txns.length));
      }
    }
    update({accounts});
    setSummary({uploadId,fileName,rows,createdIds,total:rows.reduce((s,r)=>s+r.added,0)});
    setPendingNew(unknown.map(a=>({...a,label:`${a.acctType} ...${a.acctNum.slice(-6)}`,include:true,trackSavings:false})));
  };

  // One-time setup for a never-seen account — remembered forever after this.
  const confirmNew=(a)=>{
    if(!summary)return;
    const id=uid();
    const txns=a.txns.map(t=>({...t,uploadId:summary.uploadId}));
    update(prev=>({accounts:[...prev.accounts,{id,name:a.acctNum,acctNum:a.acctNum,acctType:a.acctType,label:a.label,include:a.include,trackSavings:a.trackSavings,transactions:txns.slice(0,3000)}]}));
    setSummary(s=>({...s,rows:[...s.rows,rowFor(a.label,txns,a.txns.length)],createdIds:[...s.createdIds,id],total:s.total+txns.length}));
    setPendingNew(p=>p.filter(x=>x.acctNum!==a.acctNum));
  };
  const skipNew=acctNum=>setPendingNew(p=>p.filter(x=>x.acctNum!==acctNum));
  const updNew=(acctNum,field,val)=>setPendingNew(p=>p.map(a=>a.acctNum===acctNum?{...a,[field]:val}:a));

  const undoImport=()=>{
    const {uploadId,createdIds}=summary;
    update(prev=>({accounts:prev.accounts
      .filter(a=>!createdIds.includes(a.id))
      .map(a=>({...a,transactions:(a.transactions||[]).filter(t=>t.uploadId!==uploadId)}))}));
    setSummary(null); setPendingNew([]);
  };

  const handleFile=f=>{
    if(!f)return;
    Papa.parse(f,{header:false,skipEmptyLines:true,complete:res=>{
      const rows=res.data; if(!rows.length)return;
      const hdrs=rows[0].map(h=>String(h).trim());
      const dataRows=rows.slice(1).filter(r=>r.some(c=>String(c||"").trim()));
      setSummary(null); setPendingNew([]); setParsed(null);
      if (isRbcFormat(hdrs)) {
        importRbc(f.name,Object.values(parseRbcCsv(hdrs,dataRows,periods)));
      } else {
        setParsed({fileName:f.name,hdrs,dataRows});
        setCm(detectCols(hdrs));
        setLabel(f.name.replace(/\.(csv|tsv|xls|xlsx)$/i,""));
      }
    }});
  };

  const preview=useMemo(()=>parsed?csvToTxns(parsed.dataRows.slice(0,6),cm,periods):[],[parsed,cm,periods]);

  const saveSingle=()=>{
    if(!parsed||!label)return;
    const uploadId=uid(); const id=uid();
    const txns=csvToTxns(parsed.dataRows,cm,periods).slice(0,3000).map(t=>({...t,uploadId}));
    update(prev=>({accounts:[...prev.accounts,{id,name:parsed.fileName,label,include:inc,trackSavings:sav,transactions:txns}]}));
    setSummary({uploadId,fileName:parsed.fileName,rows:[rowFor(label,txns,txns.length)],createdIds:[id],total:txns.length});
    setParsed(null);
  };

  const togAcct=(id,field)=>update({accounts:state.accounts.map(a=>a.id===id?{...a,[field]:!a[field]}:a)});
  const delAcct=id=>update({accounts:state.accounts.filter(a=>a.id!==id)});
  const analyzeMsg=()=>{
    const from=summary.rows.map(r=>r.from).filter(Boolean).sort()[0]||"?";
    const to=summary.rows.map(r=>r.to).filter(Boolean).sort().reverse()[0]||"?";
    onAnalyze(`I just uploaded an RBC export covering ${summary.rows.length} account${summary.rows.length!==1?"s":""} with ${summary.total} new transactions from ${from} to ${to}. Please analyze: identify spending periods, flag anything unusual, highlight top spending categories across all accounts, and cross-reference with my standing rules and budget.`);
  };

  return (
    <div className="uploadWrap">
      {/* Left: saved accounts — the ONE place account settings live */}
      <div style={{width:230,flexShrink:0}}>
        <h3 style={{fontFamily:"'Outfit',sans-serif",fontSize:13,fontWeight:700,color:T.text,marginBottom:4}}>Your Accounts</h3>
        <p style={{fontSize:10.5,color:T.muted,fontFamily:"'Geist',sans-serif",marginBottom:10,lineHeight:1.5}}>Settings here stick — uploads never change them.</p>
        {!state.accounts.length&&<p style={{fontSize:11,color:T.muted,fontFamily:"'Geist',sans-serif"}}>No accounts yet — drop an export to get started.</p>}
        {state.accounts.map(a=>(
          <Card key={a.id} style={{marginBottom:8,padding:11}}>
            <div style={{fontSize:11.5,fontWeight:600,color:T.text,marginBottom:2,fontFamily:"'Geist',sans-serif"}}>{a.label||a.name}</div>
            <div style={{fontSize:10,color:T.muted,marginBottom:7,fontFamily:"'Geist',sans-serif"}}>{(a.transactions||[]).length} transactions</div>
            <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
              {[["include",a.include?"✓ Included":"Excluded",T.accent],["trackSavings",a.trackSavings?"💰 Savings":"Regular",T.green]].map(([f,l,c])=>(
                <button key={f} onClick={()=>togAcct(a.id,f)} style={{fontSize:9.5,padding:"3px 8px",borderRadius:999,border:`1px solid ${a[f]?c:T.border}`,background:a[f]?(f==="include"?T.accentDim:"rgba(24,154,85,0.08)"):"none",color:a[f]?c:T.muted,cursor:"pointer",fontFamily:"'Geist',sans-serif",fontWeight:500}}>{l}</button>
              ))}
              <button onClick={()=>delAcct(a.id)} style={{fontSize:9.5,padding:"3px 8px",borderRadius:999,border:`1px solid ${T.border}`,background:"none",color:T.red,cursor:"pointer",fontFamily:"'Geist',sans-serif"}}>Del</button>
            </div>
          </Card>
        ))}
      </div>

      {/* Right: drop zone → receipt / new-account setup */}
      <div style={{flex:1,display:"flex",flexDirection:"column",gap:12}}>
        {!parsed&&(
          <div onDragOver={e=>{e.preventDefault();setDrag(true);}} onDragLeave={()=>setDrag(false)}
            onDrop={e=>{e.preventDefault();setDrag(false);handleFile(e.dataTransfer.files[0]);}}
            onClick={()=>ref.current?.click()}
            style={{border:`2px dashed ${drag?T.accent:T.borderH}`,borderRadius:20,padding:"40px 32px",textAlign:"center",cursor:"pointer",background:drag?T.accentDim:T.surf,transition:"all .2s",boxShadow:drag?"none":"0 1px 2px rgba(27,27,43,0.04)"}}>
            <input ref={ref} type="file" accept=".csv,.tsv,.xls,.xlsx" style={{display:"none"}} onChange={e=>{handleFile(e.target.files[0]);e.target.value="";}}/>
            <div style={{width:52,height:52,borderRadius:"50%",margin:"0 auto 12px",background:"linear-gradient(135deg,#635BFF,#9A6BFF)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,boxShadow:"0 8px 20px -8px rgba(99,91,255,0.5)"}}>📥</div>
            <div style={{fontFamily:"'Outfit',sans-serif",fontSize:16,fontWeight:700,color:T.text,marginBottom:5}}>Drop your RBC export here</div>
            <div style={{fontSize:12,color:T.muted,fontFamily:"'Geist',sans-serif"}}>or click to browse · .csv and .xls · known accounts import instantly</div>
          </div>
        )}

        {/* One-time setup for never-seen accounts */}
        {pendingNew.map(a=>(
          <Card key={a.acctNum} style={{padding:14,border:`1.5px solid ${T.accent}55`}}>
            <div style={{fontSize:10,fontFamily:"'Geist',sans-serif",fontWeight:600,color:T.accent,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:8}}>✨ New account found · {a.acctType} ...{a.acctNum.slice(-6)} · {a.txns.length} transactions</div>
            <div style={{display:"flex",gap:8,alignItems:"flex-end",flexWrap:"wrap"}}>
              <div style={{flex:1,minWidth:180}}><Lbl>Name this account</Lbl><Inp value={a.label} onChange={v=>updNew(a.acctNum,"label",v)}/></div>
              <button onClick={()=>updNew(a.acctNum,"include",!a.include)} style={{fontSize:11,padding:"7px 12px",borderRadius:999,border:`1px solid ${a.include?T.accent:T.border}`,background:a.include?T.accentDim:"none",color:a.include?T.accent:T.muted,cursor:"pointer",fontFamily:"'Geist',sans-serif",fontWeight:500}}>{a.include?"✓ Include":"Exclude"}</button>
              <button onClick={()=>updNew(a.acctNum,"trackSavings",!a.trackSavings)} style={{fontSize:11,padding:"7px 12px",borderRadius:999,border:`1px solid ${a.trackSavings?T.green:T.border}`,background:a.trackSavings?"rgba(24,154,85,0.08)":"none",color:a.trackSavings?T.green:T.muted,cursor:"pointer",fontFamily:"'Geist',sans-serif",fontWeight:500}}>{a.trackSavings?"💰 Savings":"Regular"}</button>
              <Btn onClick={()=>confirmNew(a)}>Add</Btn>
              <Btn v="g" onClick={()=>skipNew(a.acctNum)}>Skip</Btn>
            </div>
            <div style={{fontSize:10,color:T.muted,fontFamily:"'Geist',sans-serif",marginTop:7}}>You'll only see this once — next uploads to this account import automatically.</div>
          </Card>
        ))}

        {/* Import receipt */}
        {summary&&(
          <Card style={{padding:16}}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
              <div style={{width:34,height:34,borderRadius:"50%",background:"rgba(24,154,85,0.1)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:16}}>✓</div>
              <div>
                <div style={{fontFamily:"'Outfit',sans-serif",fontSize:14,fontWeight:700,color:T.text}}>{summary.total} new transaction{summary.total!==1?"s":""} imported</div>
                <div style={{fontSize:10.5,color:T.muted,fontFamily:"'Geist',sans-serif"}}>{summary.fileName} · {summary.rows.length} account{summary.rows.length!==1?"s":""}</div>
              </div>
            </div>
            <div style={{display:"flex",flexDirection:"column"}}>
              {summary.rows.map((r,i)=>(
                <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 2px",borderTop:`1px solid ${T.border}55`}}>
                  <div style={{flex:1,fontSize:12,fontWeight:600,color:T.text,fontFamily:"'Geist',sans-serif"}}>{r.label}</div>
                  <div style={{fontSize:11,color:r.added?T.green:T.muted,fontFamily:"'Geist',sans-serif",fontWeight:500}}>{r.added?`+${r.added} new`:"nothing new"}</div>
                  {r.skipped>0&&<div style={{fontSize:10.5,color:T.muted,fontFamily:"'Geist',sans-serif"}}>{r.skipped} duplicate{r.skipped!==1?"s":""} skipped</div>}
                  <div style={{fontSize:10.5,color:T.muted,fontFamily:"'Geist',sans-serif",minWidth:130,textAlign:"right"}}>{r.from?`${r.from} → ${r.to}`:"—"}</div>
                </div>
              ))}
            </div>
            <div style={{display:"flex",gap:8,marginTop:12,alignItems:"center"}}>
              {summary.total>0&&<Btn onClick={analyzeMsg}>🧠 Analyze upload</Btn>}
              <Btn v="g" onClick={()=>{setSummary(null);setPendingNew([]);}}>Done</Btn>
              {summary.total>0&&<button onClick={undoImport} style={{marginLeft:"auto",fontSize:11,color:T.red,background:"none",border:"none",cursor:"pointer",fontFamily:"'Geist',sans-serif"}}>↩ Undo this import</button>}
            </div>
          </Card>
        )}

        {/* Non-RBC file: column mapping (rare path) */}
        {parsed&&(
          <Card style={{padding:14}}>
            <h3 style={{fontFamily:"'Outfit',sans-serif",fontSize:13,fontWeight:700,color:T.text,marginBottom:4}}>{parsed.fileName}</h3>
            <p style={{fontSize:11,color:T.muted,fontFamily:"'Geist',sans-serif",marginBottom:12}}>This doesn't look like an RBC export — map the columns so I can read it.</p>
            <Lbl>Column Mapping</Lbl>
            <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:8,marginBottom:14}}>
              {[["Date","dateI"],["Description","descI"],["Amount","amtI"],["Debit (–)","debI"],["Credit (+)","credI"]].map(([lbl,key])=>(
                <div key={key}><Lbl style={{fontSize:9}}>{lbl}</Lbl>
                  <Sel value={cm[key]} onChange={v=>setCm(p=>({...p,[key]:+v}))} options={[{v:-1,l:"—"},...parsed.hdrs.map((h,i)=>({v:i,l:`[${i}] ${h.slice(0,12)}`}))]} style={{fontSize:11}}/>
                </div>
              ))}
            </div>
            <Lbl>Preview</Lbl>
            <div style={{overflowX:"auto",marginBottom:14}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:11,fontFamily:"'Geist',sans-serif"}}>
                <thead><tr style={{borderBottom:`1px solid ${T.border}`}}>
                  <th style={sx.th}>Date</th><th style={sx.th}>Description</th><th style={{...sx.th,textAlign:"right"}}>Amount</th><th style={sx.th}>Period</th>
                </tr></thead>
                <tbody>{preview.map((t,i)=>(
                  <tr key={i} style={{borderBottom:`1px solid ${T.border}22`}}>
                    <td style={{...sx.td,color:T.muted}}>{t.date}</td>
                    <td style={{...sx.td,color:T.text,maxWidth:200}}>{t.description.slice(0,45)}</td>
                    <td style={{...sx.td,textAlign:"right",color:t.amount>=0?T.green:T.red}}>{fmtM(t.amount,true)}</td>
                    <td style={{...sx.td,color:T.muted,fontSize:10}}>{periods.find(p=>p.key===t.periodKey)?.label||"—"}</td>
                  </tr>
                ))}
                {!preview.length&&<tr><td colSpan={4} style={{padding:12,textAlign:"center",color:T.muted,fontSize:11}}>No rows parsed — check column mapping.</td></tr>}
                </tbody>
              </table>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr auto auto",gap:10,alignItems:"end",marginBottom:12}}>
              <div><Lbl>Account Label</Lbl><Inp value={label} onChange={setLabel} placeholder="e.g. Savings ...5110119"/></div>
              <div><Lbl>Include</Lbl>
                <button onClick={()=>setInc(p=>!p)} style={{padding:"7px 12px",borderRadius:999,border:`1px solid ${inc?T.accent:T.border}`,background:inc?T.accentDim:"none",color:inc?T.accent:T.muted,cursor:"pointer",fontFamily:"'Geist',sans-serif",fontSize:11,display:"block",width:"100%"}}>{inc?"✓ Included":"Excluded"}</button>
              </div>
              <div><Lbl>Track Savings</Lbl>
                <button onClick={()=>setSav(p=>!p)} style={{padding:"7px 12px",borderRadius:999,border:`1px solid ${sav?T.green:T.border}`,background:sav?"rgba(24,154,85,0.08)":"none",color:sav?T.green:T.muted,cursor:"pointer",fontFamily:"'Geist',sans-serif",fontSize:11,display:"block",width:"100%"}}>{sav?"💰 Yes":"No"}</button>
              </div>
            </div>
            <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
              <Btn v="g" onClick={()=>setParsed(null)}>Cancel</Btn>
              <Btn onClick={saveSingle} disabled={!label}>Save Account</Btn>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

// ── Markdown renderer ─────────────────────────────────────────────────────
function MarkdownMsg({content,isUser}) {
  const clr=isUser?"#fff":T.text;
  const mutedClr=isUser?"rgba(255,255,255,0.75)":T.muted;
  const codeBg=isUser?"rgba(0,0,0,0.2)":"rgba(37,99,235,0.07)";

  // Inline styles helpers
  const inlineStyles=(text)=>{
    // Split on bold, italic, inline code
    const parts=[];
    const re=/(`[^`]+`|\*\*[^*]+\*\*|\*[^*]+\*|_[^_]+_)/g;
    let last=0,m;
    while((m=re.exec(text))!==null){
      if(m.index>last) parts.push(<span key={last}>{text.slice(last,m.index)}</span>);
      const tok=m[0];
      if(tok.startsWith("**")) parts.push(<strong key={m.index}>{tok.slice(2,-2)}</strong>);
      else if(tok.startsWith("`")) parts.push(<code key={m.index} style={{fontFamily:"'Geist',sans-serif",fontSize:11,background:codeBg,padding:"1px 5px",borderRadius:4,color:clr}}>{tok.slice(1,-1)}</code>);
      else parts.push(<em key={m.index}>{tok.slice(1,-1)}</em>);
      last=m.index+tok.length;
    }
    if(last<text.length) parts.push(<span key={last}>{text.slice(last)}</span>);
    return parts;
  };

  const lines=content.split("\n");
  const nodes=[];
  let i=0;
  while(i<lines.length){
    const line=lines[i];
    // Fenced code block
    if(line.trimStart().startsWith("```")){
      const lang=line.replace(/^```/,"").trim();
      const codeLines=[];
      i++;
      while(i<lines.length&&!lines[i].trimStart().startsWith("```")){
        codeLines.push(lines[i]); i++;
      }
      nodes.push(
        <pre key={i} style={{background:codeBg,borderRadius:7,padding:"8px 11px",overflowX:"auto",fontFamily:"'Geist',sans-serif",fontSize:11,lineHeight:1.6,margin:"6px 0",color:clr}}>
          {lang&&<div style={{fontSize:9,color:mutedClr,marginBottom:4,textTransform:"uppercase",letterSpacing:"0.08em"}}>{lang}</div>}
          <code>{codeLines.join("\n")}</code>
        </pre>
      );
      i++; continue;
    }
    // H1/H2/H3
    const hm=line.match(/^(#{1,3})\s+(.+)/);
    if(hm){
      const sz=[16,14,13][hm[1].length-1];
      nodes.push(<div key={i} style={{fontFamily:"'Outfit',sans-serif",fontWeight:700,fontSize:sz,color:clr,margin:"8px 0 3px"}}>{inlineStyles(hm[2])}</div>);
      i++; continue;
    }
    // Horizontal rule
    if(/^---+$/.test(line.trim())){
      nodes.push(<hr key={i} style={{border:"none",borderTop:`1px solid ${mutedClr}`,margin:"8px 0"}}/>);
      i++; continue;
    }
    // GFM table: header row, separator (|---|:--:|), body rows
    if(line.trim().startsWith("|")&&i+1<lines.length&&/^\s*\|?[\s:|\-]+\|?\s*$/.test(lines[i+1])&&lines[i+1].includes("-")){
      const parseRow=l=>l.trim().replace(/^\|/,"").replace(/\|$/,"").split("|").map(c=>c.trim());
      const header=parseRow(line);
      const aligns=parseRow(lines[i+1]).map(s=>/^:-+:$/.test(s)?"center":/^-+:$/.test(s)?"right":"left");
      i+=2;
      const body=[];
      while(i<lines.length&&lines[i].trim().startsWith("|")){ body.push(parseRow(lines[i])); i++; }
      nodes.push(
        <div key={`tbl${i}`} style={{overflowX:"auto",margin:"8px 0",borderRadius:10,border:`1px solid ${isUser?"rgba(255,255,255,0.3)":T.border}`}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:11.5}}>
            <thead><tr>
              {header.map((h,j)=><th key={j} style={{padding:"7px 11px",textAlign:aligns[j]||"left",fontSize:10.5,fontWeight:600,color:mutedClr,borderBottom:`1.5px solid ${isUser?"rgba(255,255,255,0.3)":T.border}`,background:isUser?"rgba(0,0,0,0.12)":T.raised,whiteSpace:"nowrap"}}>{inlineStyles(h)}</th>)}
            </tr></thead>
            <tbody>{body.map((r,ri)=>(
              <tr key={ri}>
                {header.map((_,j)=><td key={j} style={{padding:"6px 11px",textAlign:aligns[j]||"left",borderBottom:ri<body.length-1?`1px solid ${isUser?"rgba(255,255,255,0.15)":T.border+"66"}`:"none",lineHeight:1.5}}>{inlineStyles(r[j]??"")}</td>)}
              </tr>
            ))}</tbody>
          </table>
        </div>
      );
      continue;
    }
    // Bullet list — collect consecutive items
    if(/^[\-\*]\s/.test(line)){
      const items=[];
      while(i<lines.length&&/^[\-\*]\s/.test(lines[i])){
        items.push(<li key={i} style={{marginBottom:2}}>{inlineStyles(lines[i].replace(/^[\-\*]\s/,""))}</li>);
        i++;
      }
      nodes.push(<ul key={`ul${i}`} style={{paddingLeft:18,margin:"4px 0",listStyle:"disc"}}>{items}</ul>);
      continue;
    }
    // Numbered list
    if(/^\d+\.\s/.test(line)){
      const items=[];
      while(i<lines.length&&/^\d+\.\s/.test(lines[i])){
        items.push(<li key={i} style={{marginBottom:2}}>{inlineStyles(lines[i].replace(/^\d+\.\s/,""))}</li>);
        i++;
      }
      nodes.push(<ol key={`ol${i}`} style={{paddingLeft:18,margin:"4px 0"}}>{items}</ol>);
      continue;
    }
    // Blank line → small gap
    if(!line.trim()){
      nodes.push(<div key={i} style={{height:6}}/>);
      i++; continue;
    }
    // Normal paragraph line
    nodes.push(<div key={i} style={{lineHeight:1.65}}>{inlineStyles(line)}</div>);
    i++;
  }
  return <div style={{fontFamily:"'Geist',sans-serif",fontSize:12,color:clr,overflowWrap:"break-word",minWidth:0}}>{nodes}</div>;
}

// ── Discuss Tab ────────────────────────────────────────────────────────────
const STARTERS=["Analyze my most recent period","Where am I over budget?","Project savings for next 6 months","Compare last 3 periods to budget","Review open action items","How is eating out trending?"];
// Module-level so a DiscussTab remount (tab switch mid-request) cannot start a
// second concurrent chat request. Must be reset in EVERY exit path of send().
let chatBusy=false;
// Balance fields the AI may only fill when blank — notes is the one exception.
const BAL_FIELDS=["savingsBalance","ccBalance","locBalance","paymentPlanBalance"];
const Ava=()=><div style={{width:26,height:26,borderRadius:"50%",flexShrink:0,background:"linear-gradient(135deg,#635BFF,#9A6BFF)",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:12,marginTop:2}}>✦</div>;
function DiscussTab({state,update,periods,metrics,pendingMsg,clearPending,token,onBusy}) {
  const [inp,setInp]=useState("");
  const [loading,setLoading]=useState(false);
  const [streamText,setStreamText]=useState("");
  const [suggestedRules,setSuggestedRules]=useState([]);
  const [pendingPeriodUpdates,setPendingPeriodUpdates]=useState([]);
  const [suggestedActions,setSuggestedActions]=useState([]);
  const endRef=useRef();
  const scrollRef=useRef();
  const stickRef=useRef(true); // stick to bottom unless the user scrolled up
  const msgs=state.messages||[];
  const onScroll=e=>{ const el=e.target; stickRef.current=el.scrollHeight-el.scrollTop-el.clientHeight<80; };
  const firstScroll=useRef(true);
  const [elapsed,setElapsed]=useState(0);
  useEffect(()=>{
    if(!loading){ setElapsed(0); return; }
    const t0=Date.now();
    const iv=setInterval(()=>setElapsed(Math.floor((Date.now()-t0)/1000)),1000);
    return ()=>clearInterval(iv);
  },[loading]);
  // Braces required: an implicit-return arrow hands scrollIntoView's return
  // value to React as the effect cleanup. Patched DOM prototypes (browser
  // extensions) can make it truthy → "TypeError: n is not a function" when
  // msgs.length next changes. This was the long-standing Discuss crash.
  useEffect(()=>{
    if(!stickRef.current) return;
    if(firstScroll.current){ firstScroll.current=false; if(scrollRef.current) scrollRef.current.scrollTop=scrollRef.current.scrollHeight; } // mount: jump, don't animate through the whole history
    else endRef.current?.scrollIntoView({behavior:"smooth"});
  },[msgs.length]);
  // Streaming scroll is INSTANT (scrollTop assignment) — a smooth scrollIntoView
  // restarted on every delta is what made streaming feel choppy.
  useEffect(()=>{ if(streamText&&stickRef.current&&scrollRef.current) scrollRef.current.scrollTop=scrollRef.current.scrollHeight; },[streamText]);
  useEffect(()=>{
    if(pendingMsg){ clearPending(); send(pendingMsg); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  const send=async(text)=>{
    const msg=text||inp.trim(); if(!msg||loading||chatBusy)return;
    setInp(""); setStreamText("");
    chatBusy=true; onBusy?.(true); stickRef.current=true;
    const sys=buildSys(state,periods,metrics);
    const userMsg={id:uid(),role:"user",content:msg,ts:Date.now()};
    const newMsgs=[...msgs,userMsg];
    const append=m=>update(prev=>({messages:[...(prev.messages||[]),m]}));
    update(prev=>({messages:[...(prev.messages||[]),userMsg]}));
    setLoading(true);
    try {
      const res=await fetch("/.netlify/functions/chat",{
        method:"POST",headers:{"Content-Type":"application/json","Authorization":`Bearer ${token}`},
        body:JSON.stringify({model:state.model||MDL,max_tokens:4000,system:sys,messages:newMsgs.slice(-16).map(m=>({role:m.role,content:m.content}))})
      });
      if (!res.ok) {
        // Netlify returns 502 (not 504) when a function exceeds its timeout.
        const msg=(res.status===504||res.status===502)?"Request timed out — try a shorter question or upload fewer transactions at once."
          :res.status===401?"Session expired — please sign out and sign back in."
          :`Server error (${res.status}) — check function logs.`;
        append({id:uid(),role:"assistant",content:msg,ts:Date.now()});
        setLoading(false); chatBusy=false; return;
      }
      let reply="";
      const ct=res.headers.get("content-type")||"";
      if (ct.includes("text/event-stream")&&res.body) {
        // SSE from the streaming chat function — accumulate text deltas and
        // render them live via streamText. Final commit happens once, below.
        const reader=res.body.getReader(), dec=new TextDecoder();
        let buf="", stopReason=null, errMsg=null, lastFlush=0;
        for(;;){
          const {done,value}=await reader.read(); if(done)break;
          buf+=dec.decode(value,{stream:true});
          const lines=buf.split("\n"); buf=lines.pop();
          for(const line of lines){
            if(!line.startsWith("data:"))continue;
            let ev; try{ev=JSON.parse(line.slice(5).trim())}catch{continue}
            if(ev.type==="content_block_delta"&&ev.delta?.type==="text_delta"){
              reply+=ev.delta.text;
              // Throttle preview renders to ~25/s — per-token setState made
              // scrolling feel choppy on long replies.
              const nowT=Date.now();
              if(nowT-lastFlush>40){ lastFlush=nowT; setStreamText(reply); }
            }
            else if(ev.type==="message_delta"&&ev.delta?.stop_reason) stopReason=ev.delta.stop_reason;
            else if(ev.type==="error") errMsg=ev.error?.message||"stream error";
          }
        }
        setStreamText(reply);
        if(errMsg&&!reply.trim()) reply="Error from Claude: "+errMsg;
        else if(!reply.trim()) reply=stopReason==="refusal"?"The model declined this request — try rephrasing.":"Error: no response from Claude.";
        if(stopReason==="max_tokens") reply+="\n\n_⚠️ Response hit the length limit — ask me to continue if it was cut off._";
      } else {
        // Non-streaming fallback (old function shape)
        const data=await res.json().catch(()=>null);
        reply=data?.content?.find(c=>c.type==="text")?.text||"Error: no response from Claude.";
      }
      // Parse AI projection updates
      const projMatch=reply.match(/\[AI_PROJ\]([\s\S]*?)\[\/AI_PROJ\]/);
      if (projMatch) {
        try {
          const newProj=JSON.parse(projMatch[1].trim());
          update(prev=>({aiProjections:{...(prev.aiProjections||{}),...newProj}}));
          reply=reply.replace(/\[AI_PROJ\][\s\S]*?\[\/AI_PROJ\]/, "").trim();
          const n=Object.keys(newProj).length;
          reply+=`\n\n_📊 Updated AI Proj. Balance for ${n} period${n!==1?"s":""}._`;
        } catch(e){}
      }
      // Parse period data updates
      const periodMatches=[...reply.matchAll(/\[UPDATE_PERIOD:([\d\-]+)\]([\s\S]*?)\[\/UPDATE_PERIOD\]/g)];
      if (periodMatches.length) {
        const updates=[];
        for (const pm of periodMatches) {
          try {
            const pk=pm[1].trim(), data=JSON.parse(pm[2].trim());
            const pLabel=periods.find(p=>p.key===pk)?.label||pk;
            const existing=(state.periodData||{})[pk]||{};
            // Preview only — the authoritative fill-blanks-only filter runs again
            // at apply time, since values may be entered while the banner is up.
            const safe=Object.fromEntries(Object.entries(data).filter(([k,v])=>{
              if(k==="notes") return v!=null; // notes always updatable
              return existing[k]==null&&v!=null; // balance fields: only fill blanks
            }));
            if (Object.keys(safe).length) updates.push({pk,label:pLabel,data:safe});
          } catch(e){}
        }
        if (updates.length) {
          setPendingPeriodUpdates(prev=>[...prev,...updates]);
          reply=reply.replace(/\[UPDATE_PERIOD:[\d\-]+\][\s\S]*?\[\/UPDATE_PERIOD\]/g,"").trim();
          reply+=`\n\n_📋 Period data update pending — see banner above._`;
        }
      }
      // Parse rule suggestions
      const ruleMatches=[...reply.matchAll(/\[SUGGEST_RULE:([^\]]+)\]([\s\S]*?)\[\/SUGGEST_RULE\]/g)];
      if (ruleMatches.length) {
        const newSuggestions=ruleMatches.map(m=>({id:uid(),category:m[1].trim(),rule:m[2].trim()}));
        setSuggestedRules(prev=>[...prev,...newSuggestions]);
        reply=reply.replace(/\[SUGGEST_RULE:[^\]]+\][\s\S]*?\[\/SUGGEST_RULE\]/g,"").trim();
        reply+=`\n\n_💡 ${newSuggestions.length} rule suggestion${newSuggestions.length!==1?"s":""} pending — see banner above._`;
      }
      // Parse action-item suggestions (period key optional). Like rules, these are
      // proposals only — nothing reaches state.reminders until the user accepts.
      // Key capture is permissive so a malformed period key still matches (and is
      // then dropped below) rather than leaking the raw tag into the chat.
      const actionMatches=[...reply.matchAll(/\[ADD_ACTION(?::([^\]]*))?\]([\s\S]*?)\[\/ADD_ACTION\]/g)];
      if (actionMatches.length) {
        const openTexts=new Set((state.reminders||[]).filter(r=>!r.resolved).map(r=>r.text.trim().toLowerCase()));
        const newActions=actionMatches.map(m=>{
          const pk=m[1]?.trim()||null;
          return {id:uid(),text:m[2].trim(),periodKey:periods.some(p=>p.key===pk)?pk:null};
        }).filter(a=>a.text&&!openTexts.has(a.text.toLowerCase()));
        reply=reply.replace(/\[ADD_ACTION(?::[^\]]*)?\][\s\S]*?\[\/ADD_ACTION\]/g,"").trim();
        if (newActions.length) {
          setSuggestedActions(prev=>[...prev,...newActions]);
          reply+=`\n\n_✅ ${newActions.length} action item${newActions.length!==1?"s":""} pending — see banner above._`;
        }
      }
      append({id:uid(),role:"assistant",content:reply,ts:Date.now()});
    } catch(e){ append({id:uid(),role:"assistant",content:"Error: "+e.message,ts:Date.now()}); }
    finally { setLoading(false); setStreamText(""); chatBusy=false; onBusy?.(false); }
  };


  const applyPeriodUpdate=u=>{
    update(prev=>{
      const pd={...(prev.periodData||{})};
      const existing=pd[u.pk]||{};
      // Balance fields fill blanks only — never overwrite a value the user has
      // entered since the banner appeared. notes always applies.
      const safe=Object.fromEntries(Object.entries(u.data)
        .filter(([k,v])=>!BAL_FIELDS.includes(k)||existing[k]==null));
      pd[u.pk]={...existing,...safe};
      return {periodData:pd};
    });
    setPendingPeriodUpdates(prev=>prev.filter(x=>x.pk!==u.pk));
  };
  const dismissPeriodUpdate=pk=>setPendingPeriodUpdates(prev=>prev.filter(u=>u.pk!==pk));
  const acceptRule=s=>{
    const rule={id:uid(),category:s.category,rule:s.rule,source:"ai-suggested",active:true,createdAt:new Date().toISOString().slice(0,10)};
    update({standingRules:[...(state.standingRules||[]),rule]});
    setSuggestedRules(prev=>prev.filter(r=>r.id!==s.id));
  };
  const dismissRule=id=>setSuggestedRules(prev=>prev.filter(r=>r.id!==id));
  const acceptAction=a=>{
    update(prev=>({reminders:[...(prev.reminders||[]),{id:uid(),text:a.text,periodKey:a.periodKey||null,resolved:false,ts:Date.now()}]}));
    setSuggestedActions(prev=>prev.filter(x=>x.id!==a.id));
  };
  const dismissAction=id=>setSuggestedActions(prev=>prev.filter(a=>a.id!==id));
  return (
    <div className="chatRoot" style={{display:"flex",flexDirection:"column",minHeight:420,maxWidth:860,margin:"0 auto",width:"100%"}}>
      {msgs.length>0&&(
        <div style={{display:"flex",justifyContent:"flex-end",marginBottom:6}}>
          <Btn sm v="g" onClick={()=>{if(window.confirm("Clear the entire chat history? Standing rules, period data, and everything else stays — only the conversation is removed."))update({messages:[]});}}>🗑 Clear chat</Btn>
        </div>
      )}
      {pendingPeriodUpdates.length>0&&(
        <div style={{marginBottom:8,display:"flex",flexDirection:"column",gap:6}}>
          {pendingPeriodUpdates.map(u=>(
            <div key={u.pk} style={{background:"#f0fdf4",border:"1px solid #86efac",borderRadius:8,padding:"10px 12px",display:"flex",gap:10,alignItems:"flex-start"}}>
              <div style={{flex:1}}>
                <div style={{fontSize:10,fontFamily:"'Geist',sans-serif",color:"#15803d",marginBottom:3,textTransform:"uppercase",letterSpacing:"0.08em"}}>📋 FamCFO wants to update period data · {u.label}</div>
                <div style={{fontSize:11,fontFamily:"'Geist',sans-serif",color:"#1c1917",lineHeight:1.7}}>
                  {Object.entries(u.data).map(([k,v])=>`${k}: ${fmtM(v)}`).join(" · ")}
                </div>
                <div style={{fontSize:10,color:"#6b7280",fontFamily:"'Geist',sans-serif",marginTop:3}}>Will not overwrite any values already entered.</div>
              </div>
              <div style={{display:"flex",gap:6,flexShrink:0,marginTop:2}}>
                <Btn sm onClick={()=>applyPeriodUpdate(u)}>✓ Apply</Btn>
                <Btn sm v="g" onClick={()=>dismissPeriodUpdate(u.pk)}>Dismiss</Btn>
              </div>
            </div>
          ))}
        </div>
      )}
      {suggestedRules.length>0&&(
        <div style={{marginBottom:10,display:"flex",flexDirection:"column",gap:6}}>
          {suggestedRules.map(s=>(
            <div key={s.id} style={{background:"#fefce8",border:"1px solid #fde047",borderRadius:8,padding:"10px 12px",display:"flex",gap:10,alignItems:"flex-start"}}>
              <div style={{flex:1}}>
                <div style={{fontSize:10,fontFamily:"'Geist',sans-serif",color:"#854d0e",marginBottom:3,textTransform:"uppercase",letterSpacing:"0.08em"}}>💡 FamCFO suggests a new rule · {s.category}</div>
                <div style={{fontSize:12,fontFamily:"'Geist',sans-serif",color:"#1c1917",lineHeight:1.5}}>{s.rule}</div>
              </div>
              <div style={{display:"flex",gap:6,flexShrink:0,marginTop:2}}>
                <Btn sm onClick={()=>acceptRule(s)}>✓ Add Rule</Btn>
                <Btn sm v="g" onClick={()=>dismissRule(s.id)}>Dismiss</Btn>
              </div>
            </div>
          ))}
        </div>
      )}
      {suggestedActions.length>0&&(
        <div style={{marginBottom:10,display:"flex",flexDirection:"column",gap:6}}>
          {suggestedActions.map(a=>(
            <div key={a.id} style={{background:"#eef2ff",border:"1px solid #a5b4fc",borderRadius:8,padding:"10px 12px",display:"flex",gap:10,alignItems:"flex-start"}}>
              <div style={{flex:1}}>
                <div style={{fontSize:10,fontFamily:"'Geist',sans-serif",color:"#4338ca",marginBottom:3,textTransform:"uppercase",letterSpacing:"0.08em"}}>✅ FamCFO suggests an action item{a.periodKey?` · ${periods.find(p=>p.key===a.periodKey)?.label||a.periodKey}`:""}</div>
                <div style={{fontSize:12,fontFamily:"'Geist',sans-serif",color:"#1c1917",lineHeight:1.5}}>{a.text}</div>
              </div>
              <div style={{display:"flex",gap:6,flexShrink:0,marginTop:2}}>
                <Btn sm onClick={()=>acceptAction(a)}>✓ Add Action</Btn>
                <Btn sm v="g" onClick={()=>dismissAction(a.id)}>Dismiss</Btn>
              </div>
            </div>
          ))}
        </div>
      )}
      <div ref={scrollRef} onScroll={onScroll} style={{flex:1,overflowY:"auto",display:"flex",flexDirection:"column",gap:14,padding:"4px 2px 16px"}}>
        {!msgs.length&&(
          <div style={{textAlign:"center",padding:"48px 16px 32px"}}>
            <div style={{width:56,height:56,borderRadius:"50%",margin:"0 auto 14px",background:"linear-gradient(135deg,#635BFF,#9A6BFF)",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:24,boxShadow:"0 8px 24px -8px rgba(99,91,255,0.5)"}}>✦</div>
            <div style={{fontFamily:"'Outfit',sans-serif",fontSize:24,fontWeight:800,color:T.text,marginBottom:6,letterSpacing:"-0.02em"}}>Hey Bayan 👋</div>
            <div style={{color:T.muted,fontSize:13,marginBottom:28,fontFamily:"'Geist',sans-serif"}}>Ask me about your budget, spending patterns, projections, or transactions.</div>
            <div style={{display:"flex",flexWrap:"wrap",gap:8,justifyContent:"center",maxWidth:560,margin:"0 auto"}}>
              {STARTERS.map(s=>(
                <button key={s} onClick={()=>send(s)} style={{padding:"9px 15px",borderRadius:999,border:`1px solid ${T.border}`,background:T.surf,color:T.text,cursor:"pointer",fontSize:12,fontWeight:500,fontFamily:"'Geist',sans-serif",boxShadow:"0 1px 2px rgba(27,27,43,0.05)"}}>{s}</button>
              ))}
            </div>
          </div>
        )}
        {msgs.map(m=>(
          <div key={m.id} style={{display:"flex",gap:8,justifyContent:m.role==="user"?"flex-end":"flex-start"}}>
            {m.role!=="user"&&<Ava/>}
            <div style={{maxWidth:"80%",minWidth:0,overflowWrap:"break-word",padding:"10px 14px",borderRadius:m.role==="user"?"18px 18px 6px 18px":"6px 18px 18px 18px",background:m.role==="user"?"linear-gradient(135deg,#635BFF,#7B5BFF)":T.surf,border:m.role==="user"?"none":`1px solid ${T.border}`,color:m.role==="user"?"#fff":T.text,boxShadow:m.role==="user"?"0 4px 14px -6px rgba(99,91,255,0.5)":"0 1px 2px rgba(27,27,43,0.05)"}}>
              {m.role==="user"
                ? <div style={{fontSize:13,fontFamily:"'Geist',sans-serif",lineHeight:1.6,whiteSpace:"pre-wrap",color:"#fff"}}>{m.content}</div>
                : <MarkdownMsg content={m.content} isUser={false}/>
              }
            </div>
          </div>
        ))}
        {loading&&<div style={{display:"flex",gap:8,justifyContent:"flex-start"}}>
          <Ava/>
          <div style={{maxWidth:"80%",minWidth:0,overflowWrap:"break-word",padding:"10px 14px",borderRadius:"6px 18px 18px 18px",background:T.surf,border:`1px solid ${T.border}`,color:T.text,boxShadow:"0 1px 2px rgba(27,27,43,0.05)"}}>
            {streamText
              ? <><MarkdownMsg content={streamText.split(/\[(?:AI_PROJ\]|ADD_ACTION[\]:]|UPDATE_PERIOD:|SUGGEST_RULE:)/)[0]} isUser={false}/><span style={{display:"inline-block",width:3,height:13,background:T.accent,marginLeft:2,borderRadius:2,verticalAlign:"text-bottom",animation:"fcBlink 1s step-end infinite"}}/></>
              : <div style={{display:"flex",gap:8,alignItems:"center",padding:"3px 2px"}}>
                  <span style={{display:"flex",gap:5}}>{[0,1,2].map(i=><span key={i} style={{width:7,height:7,borderRadius:"50%",background:T.accent,animation:`fcBounce 1.2s ${i*0.15}s ease-in-out infinite`}}/>)}</span>
                  {elapsed>=3&&<span style={{fontSize:11,color:T.muted,fontFamily:"'Geist',sans-serif"}}>{elapsed<15?`Thinking… ${elapsed}s`:`Thinking deeply — heavy analyses can take a minute… ${elapsed}s`}</span>}
                </div>}
          </div>
        </div>}
        <div ref={endRef}/>
      </div>
      <div>
        <div style={{display:"flex",gap:8,background:T.surf,borderRadius:22,padding:"8px 8px 8px 16px",border:`1.5px solid ${T.border}`,alignItems:"flex-end",boxShadow:"0 6px 24px -10px rgba(27,27,43,0.15)"}}>
          <textarea value={inp} rows={1}
            onChange={e=>{setInp(e.target.value);e.target.style.height="auto";e.target.style.height=Math.min(e.target.scrollHeight,120)+"px";}}
            onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();send();e.target.style.height="auto";}}}
            placeholder="Ask about your finances…"
            style={{flex:1,background:"none",border:"none",outline:"none",color:T.text,fontSize:13,fontFamily:"'Geist',sans-serif",resize:"none",minHeight:24,maxHeight:120,lineHeight:1.5,padding:"7px 0"}}/>
          <button onClick={()=>send()} disabled={!inp.trim()||loading} title="Send"
            style={{width:38,height:38,borderRadius:"50%",border:"none",flexShrink:0,cursor:(!inp.trim()||loading)?"not-allowed":"pointer",background:(!inp.trim()||loading)?T.raised:"linear-gradient(135deg,#635BFF,#7B5BFF)",color:(!inp.trim()||loading)?T.muted:"#fff",fontSize:17,display:"flex",alignItems:"center",justifyContent:"center",boxShadow:(!inp.trim()||loading)?"none":"0 2px 10px -2px rgba(99,91,255,0.5)",transition:"all .15s"}}>↑</button>
        </div>
        <div style={{textAlign:"center",fontSize:10.5,color:T.muted,fontFamily:"'Geist',sans-serif",marginTop:7,minHeight:14}}>
          {loading
            ? <span style={{color:T.accent,fontWeight:600}}>✦ FamCFO is responding — stay on this tab; switching is paused until it finishes</span>
            : "Enter to send · Shift+Enter for a new line"}
        </div>
      </div>
    </div>
  );
}

// ── Rules Tab ──────────────────────────────────────────────────────────────
function RulesTab({state,update}) {
  const [editRule,setEditRule]=useState(null);
  const [addCat,setAddCat]=useState(RULE_CATS[0]);
  const [addText,setAddText]=useState("");
  const rules=state.standingRules||[];
  const grouped=RULE_CATS.reduce((m,c)=>{m[c]=rules.filter(r=>r.category===c);return m;},{});

  const saveEdit=()=>{
    if(!editRule?.rule?.trim())return;
    update({standingRules:rules.map(r=>r.id===editRule.id?editRule:r)});
    setEditRule(null);
  };
  const delRule=id=>update({standingRules:rules.filter(r=>r.id!==id)});
  const togRule=id=>update({standingRules:rules.map(r=>r.id===id?{...r,active:!r.active}:r)});
  const addRule=()=>{
    if(!addText.trim())return;
    update({standingRules:[...rules,{id:uid(),category:addCat,rule:addText.trim(),source:"manual",active:true,createdAt:new Date().toISOString().slice(0,10)}]});
    setAddText("");
  };
  // Only offered on an empty rules list (first-run bootstrap) — never shown
  // once rules exist, so deliberately-deleted seed rules can't be re-imported.
  const importSeed=()=>{
    if(rules.length) return;
    update({standingRules:[...STANDING_RULES_SEED]});
  };

  const srcBadge=src=>({
    seed:{label:"seed",bg:"#eff6ff",color:"#1d4ed8",border:"#bfdbfe"},
    manual:{label:"manual",bg:"#f0fdf4",color:"#15803d",border:"#bbf7d0"},
    "ai-suggested":{label:"AI",bg:"#fefce8",color:"#854d0e",border:"#fde047"},
  }[src]||{label:src,bg:T.raised,color:T.muted,border:T.border});

  return (
    <div style={{display:"flex",flexDirection:"column",gap:14}}>
      {editRule&&(
        <div style={{position:"fixed",inset:0,background:"rgba(27,27,43,0.40)",backdropFilter:"blur(4px)",WebkitBackdropFilter:"blur(4px)",zIndex:200,display:"flex",alignItems:"flex-end",justifyContent:"center"}}>
          <Card className="sheet" style={{maxWidth:540,padding:18,display:"flex",flexDirection:"column",gap:12}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <h3 style={{fontFamily:"'Outfit',sans-serif",fontSize:14,fontWeight:700,color:T.text}}>Edit Rule</h3>
              <Btn v="g" sm onClick={()=>setEditRule(null)}>✕</Btn>
            </div>
            <div><Lbl>Category</Lbl><Sel value={editRule.category} onChange={v=>setEditRule(p=>({...p,category:v}))} options={RULE_CATS}/></div>
            <div><Lbl>Rule</Lbl>
              <textarea value={editRule.rule} onChange={e=>setEditRule(p=>({...p,rule:e.target.value}))}
                style={{...sx.input,minHeight:100,resize:"vertical",lineHeight:1.6}}/>
            </div>
            <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
              <Btn v="g" onClick={()=>setEditRule(null)}>Cancel</Btn>
              <Btn onClick={saveEdit}>Save</Btn>
            </div>
          </Card>
        </div>
      )}

      {/* Header + actions */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:10}}>
        <div>
          <h2 style={{fontFamily:"'Outfit',sans-serif",fontSize:17,fontWeight:700,color:T.text}}>Standing Rules</h2>
          <p style={{fontSize:11,color:T.muted,fontFamily:"'Geist',sans-serif",marginTop:3}}>
            {rules.filter(r=>r.active).length} active · {rules.length} total — sent to FamCFO on every Discuss message
          </p>
        </div>
      </div>

      {/* Add new rule */}
      <Card style={{padding:14}}>
        <Lbl style={{marginBottom:8}}>Add Rule</Lbl>
        <div style={{display:"grid",gridTemplateColumns:"160px 1fr auto",gap:8,alignItems:"start"}}>
          <Sel value={addCat} onChange={setAddCat} options={RULE_CATS} style={{height:36}}/>
          <textarea value={addText} onChange={e=>setAddText(e.target.value)} placeholder="Describe the rule — be specific and durable. E.g. 'Uber Eats charges = eating out, not groceries.'"
            style={{...sx.input,minHeight:36,resize:"vertical",lineHeight:1.5}}/>
          <Btn onClick={addRule} disabled={!addText.trim()} style={{height:36,whiteSpace:"nowrap"}}>+ Add</Btn>
        </div>
      </Card>

      {/* Rules by category */}
      {!rules.length&&(
        <Card style={{padding:32,textAlign:"center"}}>
          <div style={{fontSize:13,color:T.muted,fontFamily:"'Geist',sans-serif",marginBottom:12}}>No standing rules yet.</div>
          <Btn onClick={importSeed}>📥 Import Default Rules ({STANDING_RULES_SEED.length})</Btn>
        </Card>
      )}
      {RULE_CATS.filter(c=>grouped[c]?.length>0).map(cat=>(
        <div key={cat}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:7}}>
            <Lbl style={{marginBottom:0}}>{cat}</Lbl>
            <span style={{fontSize:10,color:T.muted,fontFamily:"'Geist',sans-serif"}}>{grouped[cat].filter(r=>r.active).length}/{grouped[cat].length} active</span>
          </div>
          <Card style={{padding:0,overflow:"hidden"}}>
            {grouped[cat].map((r,i)=>{
              const bd=srcBadge(r.source);
              return (
                <div key={r.id} style={{display:"flex",alignItems:"flex-start",gap:10,padding:"10px 12px",borderBottom:i<grouped[cat].length-1?`1px solid ${T.border}22`:"none",opacity:r.active?1:0.45,background:r.active?"transparent":T.raised}}>
                  <button onClick={()=>togRule(r.id)} style={{flexShrink:0,marginTop:2,background:"none",border:"none",cursor:"pointer",color:r.active?T.accent:T.muted,fontSize:13,padding:0,lineHeight:1}}>
                    {r.active?"●":"○"}
                  </button>
                  <div style={{flex:1,fontSize:12,fontFamily:"'Geist',sans-serif",color:T.text,lineHeight:1.6}}>{r.rule}</div>
                  <div style={{display:"flex",gap:5,flexShrink:0,alignItems:"center"}}>
                    <span style={{fontSize:9,padding:"2px 6px",borderRadius:10,background:bd.bg,color:bd.color,border:`1px solid ${bd.border}`,fontFamily:"'Geist',sans-serif"}}>{bd.label}</span>
                    <Btn sm v="g" onClick={()=>setEditRule(r)}>Edit</Btn>
                    <Btn sm v="d" onClick={()=>delRule(r.id)}>Del</Btn>
                  </div>
                </div>
              );
            })}
          </Card>
        </div>
      ))}
    </div>
  );
}

// ── Actions Tab ────────────────────────────────────────────────────────────
function ActionsTab({state,update,periods}) {
  const [text,setText]=useState("");
  const [pk,setPk]=useState("");
  const rems=state.reminders||[];
  const open=rems.filter(r=>!r.resolved),closed=rems.filter(r=>r.resolved);
  const add=()=>{if(!text.trim())return;update({reminders:[...rems,{id:uid(),text:text.trim(),periodKey:pk||null,resolved:false,ts:Date.now()}]});setText("");setPk("");};
  const resolve=id=>update({reminders:rems.map(r=>r.id===id?{...r,resolved:true}:r)});
  const del=id=>update({reminders:rems.filter(r=>r.id!==id)});
  const reopen=id=>update({reminders:rems.map(r=>r.id===id?{...r,resolved:false}:r)});
  const upcoming=periods.filter(p=>D(p.start)>=addDays(new Date(),-14)).slice(0,26);
  return (
    <div style={{maxWidth:620,margin:"0 auto",display:"flex",flexDirection:"column",gap:14}}>
      <Card style={{padding:14}}>
        <Lbl>New Reminder / Action Item</Lbl>
        <div style={{display:"flex",gap:8}}>
          <Inp value={text} onChange={setText} placeholder="e.g. Reconcile savings next period" style={{flex:1}}/>
          <Sel value={pk} onChange={setPk} options={[{v:"",l:"No period"},...upcoming.map(p=>({v:p.key,l:p.label}))]} style={{width:130}}/>
          <Btn onClick={add} disabled={!text.trim()}>Add</Btn>
        </div>
      </Card>
      <div>
        <Lbl style={{marginBottom:8}}>Open ({open.length})</Lbl>
        {!open.length&&<p style={{fontSize:11,color:T.muted,fontFamily:"'Geist',sans-serif"}}>All clear. 🎉</p>}
        {open.map(r=>(
          <Card key={r.id} style={{marginBottom:7,padding:12,display:"flex",alignItems:"flex-start",gap:12}}>
            <div style={{flex:1}}>
              <div style={{fontFamily:"'Geist',sans-serif",fontSize:12,color:T.text}}>{r.text}</div>
              {r.periodKey&&<div style={{fontSize:10,color:T.accent,fontFamily:"'Geist',sans-serif",marginTop:3}}>{periods.find(p=>p.key===r.periodKey)?.label||r.periodKey}</div>}
            </div>
            <div style={{display:"flex",gap:5,flexShrink:0}}>
              <Btn sm onClick={()=>resolve(r.id)}>✓ Done</Btn>
              <Btn sm v="d" onClick={()=>del(r.id)}>Del</Btn>
            </div>
          </Card>
        ))}
      </div>
      {closed.length>0&&<div>
        <Lbl style={{marginBottom:8}}>Resolved ({closed.length})</Lbl>
        {closed.slice(-8).reverse().map(r=>(
          <Card key={r.id} style={{marginBottom:6,padding:10,opacity:0.45,display:"flex",alignItems:"center",gap:12}}>
            <div style={{flex:1,fontFamily:"'Geist',sans-serif",fontSize:11,color:T.muted,textDecoration:"line-through"}}>{r.text}</div>
            <div style={{display:"flex",gap:5}}><Btn sm v="g" onClick={()=>reopen(r.id)}>Reopen</Btn><Btn sm v="d" onClick={()=>del(r.id)}>Del</Btn></div>
          </Card>
        ))}
      </div>}
    </div>
  );
}

// ── Main App ───────────────────────────────────────────────────────────────
// Line-art tab icons (lucide-style 24x24 stroke fragments; color via currentColor)
const TABS=[
  {id:"summary",l:"Summary",icon:<><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></>},
  {id:"income",l:"Income",icon:<><circle cx="12" cy="12" r="9"/><path d="M15.5 9a3 3 0 0 0-2.5-1.2h-2a2.2 2.2 0 1 0 0 4.4h2a2.2 2.2 0 1 1 0 4.4h-2A3 3 0 0 1 8.5 15"/><line x1="12" y1="6" x2="12" y2="18"/></>},
  {id:"expenses",l:"Expenses",icon:<><path d="M5 3v18l2-1.2L9 21l2-1.2L13 21l2-1.2L17 21l2-1.2V3l-2 1.2L15 3l-2 1.2L11 3 9 4.2 7 3 5 4.2z"/><line x1="9" y1="9" x2="15" y2="9"/><line x1="9" y1="13" x2="15" y2="13"/></>},
  {id:"upload",l:"Upload",icon:<><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></>},
  {id:"discuss",l:"Discuss",icon:<path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>},
  {id:"rules",l:"Rules",icon:<><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></>},
  {id:"actions",l:"Actions",icon:<><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.27"/></>},
  {id:"settings",l:"Settings",icon:<><line x1="21" y1="4" x2="14" y2="4"/><line x1="10" y1="4" x2="3" y2="4"/><line x1="21" y1="12" x2="12" y2="12"/><line x1="8" y1="12" x2="3" y2="12"/><line x1="21" y1="20" x2="16" y2="20"/><line x1="12" y1="20" x2="3" y2="20"/><line x1="14" y1="2" x2="14" y2="6"/><line x1="8" y1="10" x2="8" y2="14"/><line x1="16" y1="18" x2="16" y2="22"/></>},
];

// ── Settings Tab ───────────────────────────────────────────────────────────
function SettingsTab({state,update,token}) {
  const [exportModal,setExportModal]=useState(false);
  const [importErr,setImportErr]=useState("");
  const [backups,setBackups]=useState(null);      // null = not loaded, [] = none
  const [backupMsg,setBackupMsg]=useState("");
  const loadBackups=async()=>{
    setBackupMsg("Loading…");
    try {
      const res=await fetch("/.netlify/functions/state?backups=list",{headers:{Authorization:`Bearer ${token}`}});
      const keys=await res.json();
      setBackups(keys); setBackupMsg("");
    } catch { setBackupMsg("Failed to load backups."); }
  };
  const backupNow=async()=>{
    setBackupMsg("Backing up…");
    try {
      const res=await fetch("/.netlify/functions/state?backup=now",{headers:{Authorization:`Bearer ${token}`}});
      if(!res.ok){setBackupMsg("Backup failed: "+res.status);return;}
      const {key}=await res.json();
      setBackupMsg(`✓ Backed up — ${key.replace("backup-","")}`);
      if(backups!==null) loadBackups();
    } catch { setBackupMsg("Backup failed."); }
  };
  const restoreBackup=async(key)=>{
    if(!window.confirm(`Restore ${key}? This replaces ALL current data with that day's snapshot. Your current state will itself be backed up on the next save, but proceed carefully.`))return;
    setBackupMsg("Restoring…");
    try {
      const res=await fetch(`/.netlify/functions/state?restore=${encodeURIComponent(key)}`,{headers:{Authorization:`Bearer ${token}`}});
      if(!res.ok){setBackupMsg("Restore failed: "+res.status);return;}
      const snap=await res.json();
      update({...DEF,...snap});
      setBackupMsg(`✓ Restored ${key}`);
    } catch { setBackupMsg("Restore failed."); }
  };
  const fileRef=useRef();
  const exportJson=JSON.stringify(state,null,2);
  const copyExport=()=>navigator.clipboard.writeText(exportJson).then(()=>alert("Copied to clipboard!")).catch(()=>{});
  const importData=e=>{
    const file=e.target.files[0]; if(!file)return;
    const r=new FileReader();
    r.onload=ev=>{ try{ const d=JSON.parse(ev.target.result); update({...DEF,...d}); setImportErr(""); } catch{ setImportErr("Invalid file — not a FamCFO backup JSON."); } };
    r.readAsText(file); e.target.value="";
  };
  return (
    <div style={{maxWidth:560,margin:"0 auto",display:"flex",flexDirection:"column",gap:14}}>
      {exportModal&&(
        <div style={{position:"fixed",inset:0,background:"rgba(27,27,43,0.40)",backdropFilter:"blur(4px)",WebkitBackdropFilter:"blur(4px)",zIndex:200,display:"flex",alignItems:"flex-end",justifyContent:"center"}}>
          <Card className="sheet" style={{maxWidth:520,padding:18,display:"flex",flexDirection:"column",gap:12}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <h3 style={{fontFamily:"'Outfit',sans-serif",fontSize:15,fontWeight:700,color:T.text}}>Export Backup</h3>
              <Btn v="g" sm onClick={()=>setExportModal(false)}>✕</Btn>
            </div>
            <p style={{fontSize:11,color:T.muted,fontFamily:"'Geist',sans-serif"}}>Copy the JSON below and save it somewhere safe. It contains everything — budget, transactions, history, projections, rules, and messages.</p>
            <textarea readOnly value={exportJson} onFocus={e=>e.target.select()}
              style={{width:"100%",height:200,resize:"none",fontFamily:"'Geist',sans-serif",fontSize:10,background:T.bg,border:`1px solid ${T.border}`,borderRadius:8,padding:10,color:T.muted,outline:"none"}}/>
            <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
              <Btn v="g" onClick={()=>setExportModal(false)}>Close</Btn>
              <Btn onClick={copyExport}>Copy to clipboard</Btn>
            </div>
          </Card>
        </div>
      )}

      <Card style={{padding:16}}>
        <h3 style={{fontFamily:"'Outfit',sans-serif",fontSize:14,fontWeight:700,color:T.text,marginBottom:4}}>Pay Period Anchor</h3>
        <p style={{fontSize:11,color:T.muted,fontFamily:"'Geist',sans-serif",marginBottom:10}}>Any one of your actual pay dates. All periods are generated 24 back and 26 forward from this anchor.</p>
        <Inp type="date" value={state.income.anchor} onChange={v=>update({income:{...state.income,anchor:v}})}/>
        <p style={{fontSize:10,color:T.muted,fontFamily:"'Geist',sans-serif",marginTop:6}}>Current anchor: {state.income.anchor} · Changes take effect immediately across all tabs.</p>
      </Card>

      <Card style={{padding:16}}>
        <h3 style={{fontFamily:"'Outfit',sans-serif",fontSize:14,fontWeight:700,color:T.text,marginBottom:4}}>Backup &amp; Restore</h3>
        <p style={{fontSize:11,color:T.muted,fontFamily:"'Geist',sans-serif",marginBottom:12}}>Export saves a complete JSON snapshot. Restore replaces all data from a previously exported file — this cannot be undone.</p>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          <Btn onClick={()=>setExportModal(true)}>⬇ Export Backup</Btn>
          <Btn v="o" onClick={()=>fileRef.current?.click()}>⬆ Restore from Backup</Btn>
          <input ref={fileRef} type="file" accept=".json" style={{display:"none"}} onChange={importData}/>
        </div>
        {importErr&&<p style={{fontSize:11,color:T.red,fontFamily:"'Geist',sans-serif",marginTop:8}}>{importErr}</p>}
      </Card>

      <Card style={{padding:16}}>
        <h3 style={{fontFamily:"'Outfit',sans-serif",fontSize:14,fontWeight:700,color:T.text,marginBottom:4}}>AI Model</h3>
        <p style={{fontSize:11,color:T.muted,fontFamily:"'Geist',sans-serif",marginBottom:12}}>Which Claude model powers the Discuss tab. Fable 5 gives the deepest analysis but takes the longest; Opus 5 is close behind at half the cost; Sonnet 5 is fastest and cheapest.</p>
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {[
            {id:"claude-fable-5",name:"Claude Fable 5",desc:"Most capable — best for period-close analysis and complex questions (responses can take a minute)"},
            {id:"claude-opus-5",name:"Claude Opus 5",desc:"Excellent deep analysis at half Fable's cost — strong all-rounder"},
            {id:"claude-sonnet-5",name:"Claude Sonnet 5",desc:"Fast and economical — great for quick questions"},
          ].map(m=>{
            const active=(state.model||MDL)===m.id;
            return (
              <button key={m.id} onClick={()=>update({model:m.id})}
                style={{textAlign:"left",padding:"10px 12px",borderRadius:8,cursor:"pointer",background:active?T.accentDim:"none",border:`1px solid ${active?T.accent:T.border}`}}>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:12,fontWeight:600,color:active?T.accent:T.text,fontFamily:"'Geist',sans-serif"}}>{m.name}</span>
                  {active&&<span style={{fontSize:9,background:T.accent,color:"#fff",borderRadius:10,padding:"1px 7px",fontFamily:"'Geist',sans-serif"}}>ACTIVE</span>}
                </div>
                <div style={{fontSize:10,color:T.muted,fontFamily:"'Geist',sans-serif",marginTop:2}}>{m.desc}</div>
              </button>
            );
          })}
        </div>
      </Card>

      <Card style={{padding:16}}>
        <h3 style={{fontFamily:"'Outfit',sans-serif",fontSize:14,fontWeight:700,color:T.text,marginBottom:4}}>Server Backups</h3>
        <p style={{fontSize:11,color:T.muted,fontFamily:"'Geist',sans-serif",marginBottom:12}}>A snapshot is saved automatically once per day (first save of the day), and you can save one on demand any time — the last 30 are kept. Restore replaces all current data with the selected snapshot.</p>
        <div style={{display:"flex",gap:8,marginBottom:backups!==null?12:0}}>
          <Btn onClick={backupNow}>⚡ Backup now</Btn>
          {backups===null&&<Btn v="o" onClick={loadBackups}>View backups</Btn>}
        </div>
        {backups!==null&&(
          <div style={{display:"flex",flexDirection:"column",gap:6,maxHeight:220,overflowY:"auto"}}>
            {backups.length===0&&<p style={{fontSize:11,color:T.muted,fontFamily:"'Geist',sans-serif"}}>No backups yet — one is created on the first save each day.</p>}
            {backups.map(k=>(
              <div key={k} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"6px 10px",background:T.raised,borderRadius:6}}>
                <span style={{fontSize:11,fontFamily:"'Geist',sans-serif",color:T.text}}>{k.replace("backup-","")}</span>
                <Btn sm v="g" onClick={()=>restoreBackup(k)}>Restore</Btn>
              </div>
            ))}
          </div>
        )}
        {backupMsg&&<p style={{fontSize:11,color:backupMsg.startsWith("✓")?T.green:T.muted,fontFamily:"'Geist',sans-serif",marginTop:8}}>{backupMsg}</p>}
      </Card>
    </div>
  );
}

export default function FamCFO({ token, onLogout }) {
  const [state,setState]=useState(null);
  const [tab,setTab]=useState("summary");
  const [pendingMsg,setPendingMsg]=useState(null);
  const [aiBusy,setAiBusy]=useState(false); // true while a Discuss reply streams — tab switching paused so banner state isn't lost
  useEffect(()=>{
    loadSt(token).then(data=>{ if(data===null){onLogout();return;} setState(data); });
  },[token]);
  // Accepts an object patch OR a function (prev)=>patch. Anything appending to
  // an array an async callback touches (messages especially) MUST use the
  // functional form — an object patch built from a render closure clobbers
  // whatever landed while the request was in flight.
  const update=useCallback((patch)=>{
    setState(prev=>{
      const p=typeof patch==="function"?patch(prev):patch;
      const next={...prev,...p};
      saveSt(next,token);
      return next;
    });
  },[token]);
  const periods=useMemo(()=>state?.income?.anchor?genPeriods(state.income.anchor):[],[state?.income?.anchor]);
  const metrics=useMemo(()=>state?buildMetrics(state,periods):{},[state,periods]);
  const openR=(state?.reminders||[]).filter(r=>!r.resolved).length;
  const now=new Date();
  const cur=periods.find(p=>D(p.start)<=now&&D(p.end)>=now);
  const onAnalyze=useCallback((prompt)=>{ setPendingMsg(prompt); setTab("discuss"); },[]);
  if (!state) return <div style={{height:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:T.bg,color:T.muted,fontFamily:"'Geist',sans-serif",fontSize:13}}>Loading…</div>;
  return (
    <>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@500;600;700;800&family=Geist:wght@400;500;600;700&display=swap');*{box-sizing:border-box;margin:0;padding:0;font-variant-numeric:tabular-nums;}html{-webkit-font-smoothing:antialiased;}body{background:${T.bg};}::-webkit-scrollbar{width:5px;height:5px;}::-webkit-scrollbar-track{background:${T.bg};}::-webkit-scrollbar-thumb{background:${T.borderH};border-radius:3px;}input[type=number]::-webkit-inner-spin-button{opacity:.4;}select option{background:${T.surf};color:${T.text};}::selection{background:${T.accentDim};}@keyframes fcBlink{0%,100%{opacity:1}50%{opacity:0}}@keyframes fcBounce{0%,60%,100%{transform:translateY(0);opacity:.4}30%{transform:translateY(-4px);opacity:1}}@keyframes fcPulse{0%,100%{opacity:1}50%{opacity:.3}}.pageWrap{padding:16px;max-width:1100px;margin:0 auto;}.chatRoot{height:calc(100vh - 135px);height:calc(100dvh - 135px);}@keyframes fcSlideUp{from{transform:translateY(100%)}to{transform:translateY(0)}}.sheet{width:100% !important;animation:fcSlideUp .3s cubic-bezier(.32,.72,.33,1);border-radius:20px 20px 0 0 !important;max-height:88vh;max-height:88dvh;overflow-y:auto;padding-bottom:calc(20px + env(safe-area-inset-bottom)) !important;}.uploadWrap{display:flex;gap:16px;align-items:flex-start;}.tabsBottom{display:none;}@media (max-width:700px){.tabsTop{display:none !important;}.tabsBottom{display:flex;position:fixed;left:0;right:0;bottom:0;z-index:60;background:rgba(255,255,255,0.9);backdrop-filter:blur(14px);-webkit-backdrop-filter:blur(14px);border-top:1px solid ${T.border};padding:5px 4px calc(5px + env(safe-area-inset-bottom));}.pageWrap{padding:10px 10px calc(72px + env(safe-area-inset-bottom));}.chatRoot{height:calc(100vh - 128px - env(safe-area-inset-bottom));height:calc(100dvh - 128px - env(safe-area-inset-bottom));}.uploadWrap{flex-direction:column;}.uploadWrap>div:first-child{width:100% !important;}input,select,textarea{font-size:16px !important;}}`}</style>
      <div style={{minHeight:"100vh",background:T.bg,color:T.text,fontFamily:"'Geist',sans-serif"}}>
        <div style={{background:"rgba(255,255,255,0.82)",backdropFilter:"blur(14px)",WebkitBackdropFilter:"blur(14px)",borderBottom:`1px solid ${T.border}`,padding:"10px 18px",display:"flex",alignItems:"center",gap:10,position:"sticky",top:0,zIndex:50}}>
          <span style={{display:"flex",alignItems:"center",gap:8}}>
            <span style={{width:26,height:26,borderRadius:8,background:"linear-gradient(135deg,#635BFF,#9A6BFF)",boxShadow:"0 4px 10px -4px rgba(99,91,255,0.55)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="4 17 9 11 13 14 20 6"/><polyline points="14 6 20 6 20 12"/></svg>
            </span>
            <span style={{fontFamily:"'Outfit',sans-serif",fontWeight:800,fontSize:17,letterSpacing:"-0.02em",lineHeight:1}}>
              <span style={{color:T.text}}>Fam</span><span style={{background:"linear-gradient(90deg,#635BFF,#9A6BFF)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent"}}>CFO</span>
            </span>
          </span>
          <span style={{width:1,height:16,background:T.border}}/>
          <span style={{fontSize:10,color:T.muted,fontFamily:"'Geist',sans-serif"}}>{cur?.label||"—"}</span>
          <span style={{flex:1}}/>
          {cur&&<span style={{fontSize:10,color:T.muted,fontFamily:"'Geist',sans-serif"}}>Budget: <Num c={metrics[cur.key]?.bSav>=0?T.accent:T.red}>{fmtM(metrics[cur.key]?.bSav)}</Num>/period</span>}
          <button onClick={onLogout} style={{marginLeft:8,background:"none",border:`1px solid ${T.border}`,borderRadius:6,padding:"3px 9px",fontSize:10,color:T.muted,cursor:"pointer",fontFamily:"'Geist',sans-serif"}}>Sign out</button>
        </div>
        <div className="tabsTop" style={{background:"rgba(255,255,255,0.82)",backdropFilter:"blur(14px)",WebkitBackdropFilter:"blur(14px)",borderBottom:`1px solid ${T.border}`,padding:"6px 16px",display:"flex",gap:4,overflowX:"auto"}}>
          {TABS.map(t=>{
            const blocked=aiBusy&&t.id!==tab;
            return (
            <button key={t.id} onClick={()=>{ if(blocked) return; setTab(t.id); }} title={blocked?"Paused while FamCFO responds":undefined} style={{padding:"7px 14px",border:"none",borderRadius:999,cursor:blocked?"not-allowed":"pointer",opacity:blocked?0.35:1,fontFamily:"'Geist',sans-serif",fontSize:12,fontWeight:600,background:tab===t.id?T.accentDim:"transparent",color:tab===t.id?T.accent:T.muted,transition:"all .15s",whiteSpace:"nowrap",position:"relative"}}>
              {t.l}
              {t.id==="discuss"&&aiBusy&&<span style={{position:"absolute",top:4,right:4,width:6,height:6,borderRadius:"50%",background:T.accent,animation:"fcPulse 1.2s ease-in-out infinite"}}/>}
              {t.id==="actions"&&openR>0&&<span style={{position:"absolute",top:2,right:2,background:T.red,color:"#fff",fontSize:8,borderRadius:10,padding:"1px 4px",fontFamily:"'Geist',sans-serif"}}>{openR}</span>}
            </button>
            );
          })}
        </div>
        <div className="tabsBottom">
          {TABS.map(t=>{
            const blocked=aiBusy&&t.id!==tab;
            return (
              <button key={t.id} onClick={()=>{ if(blocked) return; setTab(t.id); }} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:2,padding:"4px 0 2px",border:"none",background:"none",cursor:blocked?"not-allowed":"pointer",opacity:blocked?0.35:1,position:"relative"}}>
                <span style={{display:"flex",color:tab===t.id?T.accent:T.muted}}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{t.icon}</svg>
                </span>
                <span style={{fontSize:8.5,fontFamily:"'Geist',sans-serif",fontWeight:600,color:tab===t.id?T.accent:T.muted}}>{t.l}</span>
                {t.id==="discuss"&&aiBusy&&<span style={{position:"absolute",top:1,right:"20%",width:6,height:6,borderRadius:"50%",background:T.accent,animation:"fcPulse 1.2s ease-in-out infinite"}}/>}
                {t.id==="actions"&&openR>0&&<span style={{position:"absolute",top:0,right:"16%",background:T.red,color:"#fff",fontSize:8,borderRadius:10,padding:"1px 4px",fontFamily:"'Geist',sans-serif"}}>{openR}</span>}
              </button>
            );
          })}
        </div>
        <div className="pageWrap">
          <ErrBoundary key={tab}>
            {tab==="summary"&&<SummaryTab state={state} update={update} periods={periods} metrics={metrics}/>}
            {tab==="income"&&<IncomeTab state={state} update={update}/>}
            {tab==="expenses"&&<ExpensesTab state={state} update={update} periods={periods}/>}
            {tab==="upload"&&<UploadTab state={state} update={update} periods={periods} onAnalyze={onAnalyze}/>}
            {tab==="discuss"&&<DiscussTab state={state} update={update} periods={periods} metrics={metrics} pendingMsg={pendingMsg} clearPending={()=>setPendingMsg(null)} token={token} onBusy={setAiBusy}/>}
            {tab==="rules"&&<RulesTab state={state} update={update}/>}
            {tab==="actions"&&<ActionsTab state={state} update={update} periods={periods}/>}
            {tab==="settings"&&<SettingsTab state={state} update={update} token={token}/>}
          </ErrBoundary>
        </div>
      </div>
    </>
  );
}
